import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Switch from "../../components/Switch/Switch";
import {
  changeFreeTrialStatus,
  dashboardGraphApi,
  dashboardSupportInsightsApi,
  fetchDashboardDetailsApi,
} from "../../services/dashboard";
import {
  fetchQuickStatsApi,
  fetchUserAnalyticsApi,
  fetchRevenueAnalyticsApi,
  fetchLast10DaysDataApi,
  fetchHourlyBreakdownApi,
  fetchRegistrationRechargeFunnelApi,
  fetchLastDaysDataApiGroup,
  fetchUserConsultantOnlineGapApi,
  fetchTopConsultantPerformanceApi,
  fetchTopConsultantOnlineTimeApi,
} from "../../services/enhancedDashboard";
import { getUserStatisticsApi } from "../../services/userManagement";
import { DatePicker } from "../../components/Form";
import { SecondaryButton } from "../../components/Button/Button";
import Loader from "../../comman/Loader";
import SimpleChartGrid from "../../components/charts/SimpleChartGrid";
import scrollToUp from "../../utils/scrollToUp";
import { toastMessage } from "../../utils/toastMessage";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import ModalBox from "../../components/modal/ModalBox";
import useButtonLoader from "../../hooks/UseButtonLoader";
import ReactDatePicker from "react-datepicker";
import { dateFormatUtc } from "../../utils/dateFormat";
import {
  DASHBOARD_DAY_RANGE_OPTIONS,
  buildDashboardDaysDateRange,
  buildDashboardFilterDateRange,
  formatDashboardFilterDateRangeLabel,
} from "../../utils/dashboardDateRange";
import { FormControl, InputLabel, Select as MuiSelect, MenuItem, TextField, TablePagination, Typography, Box, CircularProgress, Tooltip } from "@mui/material";
import { getDailyBreakdownColumnDefinitions, buildDailyBreakdownColumnVisibilityDefaults } from "./dashboardDailyBreakdownColumns";
import {
  getHourlyBreakdownColumnDefinitions,
  buildHourlyBreakdownColumnVisibilityDefaults,
  getDerivedCellValue,
  getDerivedTotalsValue,
  isDerivedColumn,
  computeCustomerFlowScore,
  getAttemptToFirstRechargeBreakdown,
  getAttemptToFirstRechargeTotals,
  getPeakFlowMeta,
  PEAK_FLOW_LEVELS,
} from "./dashboardHourlyBreakdownColumns";
import { getDailyGroupColumnDefinitions, buildDailyGroupColumnVisibilityDefaults, normalizeDailyGroupPerformanceData, formatGroupLabel, getConsultantManagementGroupParam } from "./dashboardDailyGroupColumns";
import ConsultationDistributionMonthlyChart from "../../components/charts/ConsultationDistributionMonthlyChart";
import FiveConsultantChart from "../../components/charts/FiveConsultantChart";
import ConsultantOnlineTimeChart from "../../components/charts/ConsultantOnlineTimeChart";
import UserGrowthChart from "../../components/charts/UserGrowthChart";
import UserConsultantOnlineGapChart from "../../components/charts/UserConsultantOnlineGapChart";

const HOURLY_INTERVAL_OPTIONS = [
  { value: "15min", label: "15 Minutes" },
  { value: "30min", label: "30 Minutes" },
  { value: "1hour", label: "1 Hour" },
  { value: "2hour", label: "2 Hours" },
  { value: "5hour", label: "5 Hours" },
  { value: "7hour", label: "7 Hours" },
  { value: "10hour", label: "10 Hours" },
  { value: "12hour", label: "12 Hours" },
];

const Dashboard = ({ isDashboard2 = false }) => {
  const navigate = useNavigate();
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Helper functions for last 5 months data
  const getLastFiveMonths = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentDate = new Date();
    const lastFiveMonths = [];
    
    for (let i = 4; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      lastFiveMonths.push(months[date.getMonth()]);
    }
    
    return lastFiveMonths;
  };

  const getLastFiveMonthsData = () => {
    const baseData = [120, 135, 150, 140, 160, 175, 190, 185, 200, 210, 195, 220];
    const currentMonth = new Date().getMonth();
    const lastFiveMonthsData = [];
    
    for (let i = 4; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      lastFiveMonthsData.push(baseData[monthIndex]);
    }
    
    return lastFiveMonthsData;
  };
  const currentDate = new Date();
  // Default: today + last 2 days = total 3 days
  const twoDaysBefore = new Date(
    currentDate.getTime() - 2 * 24 * 60 * 60 * 1000
  );
  const [startDate, setStartDate] = useState(twoDaysBefore);
  const [endDate, setEndDate] = useState(new Date());
  const [dashboardDetail, setDashboardDetail] = useState({});
  const [status, setStatus] = useState(null);
  const [statusModal, setStatusModal] = useState(false);
  const [buttonref, buttonLoader] = useButtonLoader("Yes");
  
  // Enhanced Dashboard States
  const [kpiData, setKpiData] = useState({
    totalSignup: 0,
    totalSignout: 0,
    signupPercentage: 0,
    signoutPercentage: 0,
    totalFreeConsultation: 0,
    totalPaidConsultation: 0,
    firstOfferConsultation: 0,
    firstOfferConversionPercentage: 0,
    secondOfferConsultation: 0,
    secondOfferConversionPercentage: 0,
    repeatConsultation: 0,
    repeatConsultationPercentage: 0,
    totalRecharge: 0,
    totalSpending: 0,
    totalWalletBalance: 0,
    totalInstall: 0,
    totalUninstall: 0,
  });
  
  // Top Consultant Performance States
  const [topConsultantPerformance, setTopConsultantPerformance] = useState([]);
  const [topConsultantOnlineTime, setTopConsultantOnlineTime] = useState([]);
  const [loadingTopConsultants, setLoadingTopConsultants] = useState(false);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  const [hourlyBreakdown, setHourlyBreakdown] = useState([]);
  const [registrationRechargeFunnel, setRegistrationRechargeFunnel] = useState({ rows: [], totals: null });
  const [hourlyInterval, setHourlyInterval] = useState("2hour");
  const [hourlySelectedDate, setHourlySelectedDate] = useState(() => new Date());
  const [dailyGroupPerformance, setDailyGroupPerformance] = useState([]);
  const [dailyGroupTotals, setDailyGroupTotals] = useState(null);
  // Within 7/14/28 Day - unique users who completed at least one consultation in last 7, 14, 28 days (from API)
  const [within7DayUserCount, setWithin7DayUserCount] = useState(0);
  const [within14DayUserCount, setWithin14DayUserCount] = useState(0);
  const [within28DayUserCount, setWithin28DayUserCount] = useState(0);
  const [timeFilter, setTimeFilter] = useState("today");
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // How many days to show in Daily Breakdown (default 3); user can select 2, 3, 10, 20, 30
  const [dailyBreakdownDays, setDailyBreakdownDays] = useState(3);
  // Daily Group Performance filter (Today / Yesterday / 3 day / … / Custom date)
  const [dailyGroupFilter, setDailyGroupFilter] = useState("today");
  const [dailyGroupCustomStart, setDailyGroupCustomStart] = useState(null);
  const [dailyGroupCustomEnd, setDailyGroupCustomEnd] = useState(null);
  const [rechargeFunnelFilter, setRechargeFunnelFilter] = useState("7");
  const [rechargeFunnelCustomStart, setRechargeFunnelCustomStart] = useState(null);
  const [rechargeFunnelCustomEnd, setRechargeFunnelCustomEnd] = useState(null);
  // Pagination states for daily breakdown table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageHourly, setPageHourly] = useState(0);
  const [rowsPerPageHourly, setRowsPerPageHourly] = useState(12);
  const [pageGroup, setPageGroup] = useState(0);
  const [rowsPerPageGroup, setRowsPerPageGroup] = useState(10);
  const [pageRechargeFunnel, setPageRechargeFunnel] = useState(0);
  const [rowsPerPageRechargeFunnel, setRowsPerPageRechargeFunnel] = useState(10);
  
  // Column visibility state for daily breakdown table (same columns on main + marketing dashboards)
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildDailyBreakdownColumnVisibilityDefaults()
  );
  const [hourlyColumnVisibility, setHourlyColumnVisibility] = useState(() =>
    buildHourlyBreakdownColumnVisibilityDefaults()
  );
  const [columnVisibilityGroup, setColumnVisibilityGroup] = useState(() =>
    buildDailyGroupColumnVisibilityDefaults()
  );
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [isColumnSelectorOpenHourly, setIsColumnSelectorOpenHourly] = useState(false);
  const [isColumnSelectorOpenGroup, setIsColumnSelectorOpenGroup] = useState(false);
  const columnSelectorRef = useRef(null);
  const columnSelectorRefHourly = useRef(null);
  const columnSelectorRefGroup = useRef(null);
  
  // Accordion-style show/hide per section (UI only, logic unchanged) - by default all hidden
  const [sectionOpen, setSectionOpen] = useState({
    kpiCards: false,
    userStatistics: false,
    liveOnline: true,
    dailyBreakdown: true,
    hourlyBreakdown: true,
    registrationRechargeFunnel: true,
    dailyGroupPerformance: true,
    topConsultantOnlineTime: false,
    fiveConsultantChart: false,
    consultationDistributionMonthly: false,
    userConsultantOnlineGap: false,
    userGrowthChart: false,
    revenueAnalytics: false,
  });
  const toggleSection = (key) => setSectionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  
  // Material-UI Time Range States
  const [timeRange, setTimeRange] = useState(null);
  const [timeRangeStartDate, setTimeRangeStartDate] = useState(null);
  const [timeRangeEndDate, setTimeRangeEndDate] = useState(null);
  
  // User and Consultant Online Gap Chart States
  const [onlineGapFilter, setOnlineGapFilter] = useState('today');
  const [onlineGapSelectedDate, setOnlineGapSelectedDate] = useState(null);
  const [onlineGapStartDate, setOnlineGapStartDate] = useState(null);
  const [onlineGapEndDate, setOnlineGapEndDate] = useState(null);
  const [onlineGapInterval, setOnlineGapInterval] = useState('2hour'); // Default to 2 hours
  const [onlineGapData, setOnlineGapData] = useState({
    timeIntervals: [],
    usersOnline: [],
    consultantsOnline: [],
    missedConsultations: [],
    cancelledConsultations: [],
    dateLabel: '',
    currentLiveUsers: 0,
    currentLiveConsultants: 0
  });

  // Top Consultants Chart State
  const [topConsultantsCount, setTopConsultantsCount] = useState(5); // Default to Top 5

  // Loading states for each section
  const [loadingKPIData, setLoadingKPIData] = useState(false);
  const [loadingDailyBreakdown, setLoadingDailyBreakdown] = useState(false);
  const [loadingHourlyBreakdown, setLoadingHourlyBreakdown] = useState(false);
  const [loadingRegistrationRechargeFunnel, setLoadingRegistrationRechargeFunnel] = useState(false);
  const [loadingDailyGroupPerformance, setLoadingDailyGroupPerformance] = useState(false);
  const [loadingChartAnalytics, setLoadingChartAnalytics] = useState(false);
  const [loadingRevenueData, setLoadingRevenueData] = useState(false);
  const [loadingOnlineGap, setLoadingOnlineGap] = useState(false);
  
  // Current time state for 12-hour format display
  const [currentTime, setCurrentTime] = useState(new Date());
  const didSkipInitialDateEffectRef = useRef(false);
  const rechargeFunnelRequestIdRef = useRef(0);
  
  // User Statistics state
  const [userStatistics, setUserStatistics] = useState({
    date: "",
    dateStr: "",
    totalDownloads: 0,
    consulted: 0,
    uniqueUsersResponded: 0,
    consultantResponded: 0,
    consultantNotResponded: 0,
    triedButNotRespondedPercentage: 0,
    didNotTryCount: 0,
    didNotTryPercentage: 0,
    uninstall: 0,
    freeChatConsulted: 0,
  });
  const [statisticsDate, setStatisticsDate] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  
  // Time Range Options
  const timeRangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 days" },
    { value: "last_10_days", label: "Last 10 days" },
    { value: "custom", label: "Custom Range" }
  ];
  const [chartAnalyticsData, setChartAnalyticsData] = useState({
    userGrowth: {
      dates: [],
      signups: [],
      activeUsers: [],
      totalUsers: []
    },
    fiveConsultants: {
      consultantData: [
        { name: 'Dr. Rajesh Kumar', value: 45, sessions: 120 },
        { name: 'Dr. Priya Sharma', value: 35, sessions: 95 },
        { name: 'Dr. Amit Patel', value: 25, sessions: 78 },
        { name: 'Dr. Sunita Singh', value: 20, sessions: 65 },
        { name: 'Dr. Vikram Mehta', value: 15, sessions: 52 }
      ]
    },
    consultationDistributionMonthly: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      freeConsultations: [120, 135, 150, 140, 160, 175, 190, 185, 200, 210, 195, 220],
      paidConsultations: [80, 90, 95, 100, 110, 120, 130, 125, 140, 150, 145, 160],
      failedConsultations: [15, 18, 20, 22, 25, 28, 30, 32, 35, 38, 40, 42],
      repeatConsultations: [45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100]
    }
  });
  const [chartData, setChartData] = useState({
    series: [
      {
        name: "Revenue",
        data: [50000, 60000, 70000, 80000, 90000, 85000, 95000, 100000, 110000, 105000],
      },
      {
        name: "Spending",
        data: [30000, 35000, 40000, 45000, 50000, 48000, 52000, 55000, 60000, 58000],
      },
    ],
    options: {
      chart: {
        type: "area",
        height: 350,
        toolbar: {
          show: false
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800
        }
      },
      colors: ["#3b82f6", "#ef4444"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'],
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px',
            fontWeight: '500'
          }
        }
      },
      yaxis: {
      title: {
          text: "Amount (₹)",
          style: {
            color: '#666',
            fontSize: '14px',
            fontWeight: '500'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '12px',
            fontWeight: '500'
          },
          formatter: function (val) {
            return "₹" + (val / 1000) + "K";
          }
        }
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.1,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const category = w.globals.labels[dataPointIndex];
          const revenueValue = series[0][dataPointIndex];
          const spendingValue = series[1][dataPointIndex];
          const seriesName = seriesIndex === 0 ? 'Revenue' : 'Spending';
          const seriesColor = seriesIndex === 0 ? '#3b82f6' : '#ef4444';
          
          return `
            <div style="padding: 12px; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-left: 4px solid ${seriesColor};">
              <div style="font-weight: 600; color: #242424; margin-bottom: 8px;">${category}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #666;">Revenue:</span>
                <span style="color: #3b82f6; font-weight: 600;">₹${revenueValue?.toLocaleString()}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="color: #666;">Spending:</span>
                <span style="color: #ef4444; font-weight: 600;">₹${spendingValue?.toLocaleString()}</span>
              </div>
            </div>
          `;
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '14px',
        fontWeight: 600,
        markers: {
          width: 12,
          height: 12,
          radius: 6
        }
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300
            }
          }
        }
      ]
    },
  });
  const [supportInsights, setSupportInsights] = useState({});

  //------------------request-listing-Api----------------------------------
  const getDashboardList = async () => {
    try {
      showLoader();
      setDashboardDetail({});
      const { data } = await fetchDashboardDetailsApi();
      if (data.code === 200) {
        setDashboardDetail(data?.result || {});
        scrollToUp();
      } else {
        setDashboardDetail({});
        toastMessage(data.message, "error", "offersList");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      hideLoader();
    }
  };

  //--------------support-insights-Api----------------------------------
  const getSupportInsights = async () => {
    try {
      const { data } = await dashboardSupportInsightsApi();
      if (data.code === 200) {
        setSupportInsights(data?.result || {});
      } else {
        toastMessage(data.message, "error", "supportInsights");
      }
    } catch (error) {
      console.log("error", error);
    }
  }

  //--------------fetch-kpi-data----------------------------------
  const fetchKPIData = async () => {
    try {
      setLoadingKPIData(true);
      const sendData = {
        timeRange: timeRange || 'today',
      };
      if (timeRangeStartDate) {
        sendData.timeRangeStartDate = timeRangeStartDate;
      }
      if (timeRangeEndDate) {
        sendData.timeRangeEndDate = timeRangeEndDate;
      }
      
      const { data } = await fetchQuickStatsApi(sendData);
      if (data.code === 200) {
        setKpiData(data?.result || {
          totalSignup: 0,
          totalSignout: 0,
          signupPercentage: 0,
          signoutPercentage: 0,
          totalFreeConsultation: 0,
          totalPaidConsultation: 0,
          firstOfferConsultation: 0,
          firstOfferConversionPercentage: 0,
          secondOfferConsultation: 0,
          secondOfferConversionPercentage: 0,
          repeatConsultation: 0,
          repeatConsultationPercentage: 0,
          totalRecharge: 0,
          totalSpending: 0,
          totalWalletBalance: 0,
          totalInstall: 0,
          totalUninstall: 0,
        });
      } else {
        toastMessage(data.message, "error", "kpiData");
        // Set default values on API error
        setKpiData({
          totalSignup: 0,
          totalSignout: 0,
          signupPercentage: 0,
          signoutPercentage: 0,
          totalFreeConsultation: 0,
          totalPaidConsultation: 0,
          firstOfferConsultation: 0,
          firstOfferConversionPercentage: 0,
          secondOfferConsultation: 0,
          secondOfferConversionPercentage: 0,
          repeatConsultation: 0,
          repeatConsultationPercentage: 0,
          totalRecharge: 0,
          totalSpending: 0,
          totalWalletBalance: 0,
          totalInstall: 0,
          totalUninstall: 0,
        });
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load KPI data", "error", "kpiData");
      // Set default values on error
      setKpiData({
        totalSignup: 0,
        totalSignout: 0,
        signupPercentage: 0,
        signoutPercentage: 0,
        totalFreeConsultation: 0,
        totalPaidConsultation: 0,
        firstOfferConsultation: 0,
        firstOfferConversionPercentage: 0,
        secondOfferConsultation: 0,
        secondOfferConversionPercentage: 0,
        repeatConsultation: 0,
        repeatConsultationPercentage: 0,
        totalRecharge: 0,
        totalSpending: 0,
        totalWalletBalance: 0,
        totalInstall: 0,
        totalUninstall: 0,
      });
    } finally {
      setLoadingKPIData(false);
    }
  }

  //--------------fetch-daily-breakdown----------------------------------
  const buildLastDaysSendData = (
    daysOrFilter,
    override = null,
    { isFilterPreset = false, customStartDate = null, customEndDate = null } = {}
  ) => {
    let sendData = {};
    const valueToUse = override ?? daysOrFilter;

    if (timeRange) {
      sendData.timeRange = timeRange;

      if (timeRange === 'custom') {
        if (timeRangeStartDate) {
          sendData.startDate = timeRangeStartDate;
        }
        if (timeRangeEndDate) {
          sendData.endDate = timeRangeEndDate;
        }
      } else if (timeRangeStartDate && timeRangeEndDate) {
        sendData.startDate = timeRangeStartDate;
        sendData.endDate = timeRangeEndDate;
      }
    } else if (isCustomDateRange && startDate && endDate) {
      sendData.timeFilter = timeFilter;
      sendData.startDate = dateFormatUtc(startDate);
      sendData.endDate = dateFormatUtc(endDate);
    } else if (isFilterPreset) {
      const range = buildDashboardFilterDateRange(valueToUse, {
        customStartDate,
        customEndDate,
      });
      if (!range) return null;
      Object.assign(sendData, range, { fast: true });
    } else {
      Object.assign(sendData, buildDashboardDaysDateRange(valueToUse), { fast: true });
    }

    return sendData;
  };

  // overrideDays: when user selects "Show last X days", pass X so we fetch that many without waiting for state
  const fetchDailyBreakdown = async (overrideDays = null) => {
    try {
      setLoadingDailyBreakdown(true);
      const sendData = buildLastDaysSendData(dailyBreakdownDays, overrideDays);
      
      const { data } = await fetchLast10DaysDataApi(sendData);
      if (data.code === 200) {
        const result = data?.result || {};
        const dailyBreakdownData = result.dailyBreakdown;
        setDailyBreakdown(Array.isArray(dailyBreakdownData) ? dailyBreakdownData : []);
        // Within 7/14/28 Day - unique users who completed at least one consultation in last 7, 14, 28 days
        setWithin7DayUserCount(result.within7DayUserCount ?? 0);
        setWithin14DayUserCount(result.within14DayUserCount ?? 0);
        setWithin28DayUserCount(result.within28DayUserCount ?? 0);
        // Reset pagination when data changes
        setPage(0);
      } else {
        toastMessage(data.message, "error", "dailyBreakdown");
        setDailyBreakdown([]);
        setWithin7DayUserCount(0);
        setWithin14DayUserCount(0);
        setWithin28DayUserCount(0);
        setPage(0);
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load daily breakdown data", "error", "dailyBreakdown");
      setDailyBreakdown([]);
      setWithin7DayUserCount(0);
      setWithin14DayUserCount(0);
      setWithin28DayUserCount(0);
    } finally {
      setLoadingDailyBreakdown(false);
    }
  }

  const fetchHourlyBreakdown = async (overrideDate = null, overrideInterval = null) => {
    try {
      setLoadingHourlyBreakdown(true);
      const dateToUse = overrideDate || hourlySelectedDate || new Date();
      const sendData = {
        ...buildDashboardDaysDateRange(1, dateToUse),
        intervalGap: overrideInterval || hourlyInterval,
        fast: true,
      };
      const { data } = await fetchHourlyBreakdownApi(sendData);
      if (data.code === 200) {
        const hourlyBreakdownData = data?.result?.hourlyBreakdown;
        setHourlyBreakdown(Array.isArray(hourlyBreakdownData) ? hourlyBreakdownData : []);
        setPageHourly(0);
      } else {
        toastMessage(data.message, "error", "hourlyBreakdown");
        setHourlyBreakdown([]);
        setPageHourly(0);
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load hourly breakdown data", "error", "hourlyBreakdown");
      setHourlyBreakdown([]);
      setPageHourly(0);
    } finally {
      setLoadingHourlyBreakdown(false);
    }
  }

  const fetchRegistrationRechargeFunnel = async (overrideFilter = null, overrideCustom = null) => {
    const requestId = ++rechargeFunnelRequestIdRef.current;
    try {
      setLoadingRegistrationRechargeFunnel(true);
      const filterToUse = overrideFilter ?? rechargeFunnelFilter;
      const customStart = overrideCustom?.start !== undefined
        ? overrideCustom.start
        : rechargeFunnelCustomStart;
      const customEnd = overrideCustom?.end !== undefined
        ? overrideCustom.end
        : rechargeFunnelCustomEnd;
      const fullDayRange = buildDashboardFilterDateRange(filterToUse, {
        customStartDate: customStart,
        customEndDate: customEnd,
      });
      if (!fullDayRange) return;
      const { data } = await fetchRegistrationRechargeFunnelApi(fullDayRange);
      if (requestId !== rechargeFunnelRequestIdRef.current) return;
      if (data.code === 200) {
        setRegistrationRechargeFunnel({
          rows: Array.isArray(data?.result?.rows) ? data.result.rows : [],
          totals: data?.result?.totals || null,
        });
        setPageRechargeFunnel(0);
      } else {
        setRegistrationRechargeFunnel({ rows: [], totals: null });
        toastMessage(data.message, "error", "registrationRechargeFunnel");
      }
    } catch (error) {
      if (requestId !== rechargeFunnelRequestIdRef.current) return;
      console.log("error", error);
      setRegistrationRechargeFunnel({ rows: [], totals: null });
      toastMessage("Failed to load registration recharge funnel", "error", "registrationRechargeFunnel");
    } finally {
      if (requestId === rechargeFunnelRequestIdRef.current) {
        setLoadingRegistrationRechargeFunnel(false);
      }
    }
  }

  const fetchDailyGroupPerformance = async (
    overrideFilter = null,
    overrideCustom = null
  ) => {
    try {
      setLoadingDailyGroupPerformance(true);
      const filterToUse = overrideFilter ?? dailyGroupFilter;
      const customStart =
        overrideCustom?.start !== undefined
          ? overrideCustom.start
          : dailyGroupCustomStart;
      const customEnd =
        overrideCustom?.end !== undefined
          ? overrideCustom.end
          : dailyGroupCustomEnd;

      if (
        filterToUse === "custom" &&
        (!customStart || !customEnd)
      ) {
        setLoadingDailyGroupPerformance(false);
        return;
      }

      const sendData = buildLastDaysSendData(filterToUse, null, {
        isFilterPreset: true,
        customStartDate: customStart,
        customEndDate: customEnd,
      });
      if (!sendData) {
        setLoadingDailyGroupPerformance(false);
        return;
      }

      const { data } = await fetchLastDaysDataApiGroup(sendData);
      if (data.code === 200) {
        const result = data?.result || {};
        const groupData = normalizeDailyGroupPerformanceData(result);
        setDailyGroupPerformance(groupData);
        setDailyGroupTotals(result?.totals || null);
        setPageGroup(0);
      } else {
        toastMessage(data.message, "error", "dailyGroupPerformance");
        setDailyGroupPerformance([]);
        setDailyGroupTotals(null);
        setPageGroup(0);
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load daily group performance data", "error", "dailyGroupPerformance");
      setDailyGroupPerformance([]);
      setDailyGroupTotals(null);
    } finally {
      setLoadingDailyGroupPerformance(false);
    }
  }

  //--------------fetch-revenue-data----------------------------------
  const fetchRevenueData = async () => {
    try {
      setLoadingRevenueData(true);
      let sendData = {};
      
      // Use time range parameters if available, otherwise fall back to old parameters
      if (timeRange) {
        sendData.timeRange = timeRange;
      } else {
        sendData.timeFilter = timeFilter;
      }
      
      if (timeRangeStartDate) {
        sendData.startDate = timeRangeStartDate;
      } else if (isCustomDateRange) {
        sendData.startDate = dateFormatUtc(startDate);
      }
      
      if (timeRangeEndDate) {
        sendData.endDate = timeRangeEndDate;
      } else if (isCustomDateRange) {
        sendData.endDate = dateFormatUtc(endDate);
      }
      
      const { data } = await fetchRevenueAnalyticsApi(sendData);
      if (data.code === 200) {
        const revenueData = data?.result || {};
        
        // Handle different data structures from backend
        let revenueDataArray = [];
        let spendingDataArray = [];
        let categoriesArray = [];
        
        if (Array.isArray(revenueData.revenueData) && Array.isArray(revenueData.spendingData) && Array.isArray(revenueData.categories)) {
          // New structure with revenueData, spendingData and categories arrays
          revenueDataArray = revenueData.revenueData;
          spendingDataArray = revenueData.spendingData;
          categoriesArray = revenueData.categories;
        } else if (Array.isArray(revenueData.revenueByService)) {
          // Current backend structure with revenueByService
          revenueDataArray = revenueData.revenueByService.map(item => item.value || 0);
          spendingDataArray = revenueData.revenueByService.map(item => (item.value || 0) * 0.6); // Mock spending data
          categoriesArray = revenueData.revenueByService.map(item => item.name || 'Unknown');
        } else {
          // Fallback to default data
          revenueDataArray = [50000, 60000, 70000, 80000, 90000, 85000, 95000, 100000, 110000, 105000];
          spendingDataArray = [30000, 35000, 40000, 45000, 50000, 48000, 52000, 55000, 60000, 58000];
          categoriesArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
        }
        
        setChartData(prevChartData => ({
          series: [
            {
              name: "Revenue",
              data: revenueDataArray,
            },
            {
              name: "Spending",
              data: spendingDataArray,
            },
          ],
          options: {
            ...prevChartData.options,
            xaxis: {
              ...prevChartData.options.xaxis,
              categories: categoriesArray,
            }
          }
        }));
      } else {
        toastMessage(data.message, "error", "revenueData");
        // Set fallback data on API error
        setChartData(prevChartData => ({
          ...prevChartData,
          series: [
            {
              name: "Revenue",
              data: [50000, 60000, 70000, 80000, 90000, 85000, 95000, 100000, 110000, 105000],
            },
            {
              name: "Spending",
              data: [30000, 35000, 40000, 45000, 50000, 48000, 52000, 55000, 60000, 58000],
            },
          ]
        }));
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load revenue data", "error", "revenueData");
      // Set fallback data on error
      setChartData(prevChartData => ({
        ...prevChartData,
        series: [
          {
            name: "Revenue",
            data: [50000, 60000, 70000, 80000, 90000, 85000, 95000, 100000, 110000, 105000],
          },
          {
            name: "Spending",
            data: [30000, 35000, 40000, 45000, 50000, 48000, 52000, 55000, 60000, 58000],
          },
        ]
      }));
    } finally {
      setLoadingRevenueData(false);
    }
  };

  //--------------fetch-chart-analytics-data----------------------------------
  const fetchChartAnalyticsData = async () => {
    try {
      setLoadingChartAnalytics(true);
      let sendData = {};
      
      if (timeRange) {
        sendData.timeRange = timeRange;
      }
      if (timeRangeStartDate) {
        sendData.startDate = timeRangeStartDate;
      }
      if (timeRangeEndDate) {
        sendData.endDate = timeRangeEndDate;
      }
      
      // Add topN parameter for consultants chart
      sendData.topN = topConsultantsCount;
      
      // Fetch user analytics for charts
      const userAnalytics = await fetchUserAnalyticsApi(sendData);

      if (userAnalytics.data.code === 200) {
        const analyticsData = userAnalytics.data?.result || {};
        console.log("User Analytics Data:", analyticsData);
        
        console.log('Raw fiveConsultants data:', analyticsData.fiveConsultants);
        console.log('First consultant sample:', analyticsData.fiveConsultants?.consultantData?.[0]);
        
        setChartAnalyticsData({
          userGrowth: {
            dates: Array.isArray(analyticsData.userGrowth?.dates) ? analyticsData.userGrowth.dates : [],
            signups: Array.isArray(analyticsData.userGrowth?.signups) ? analyticsData.userGrowth.signups : [],
            activeUsers: Array.isArray(analyticsData.userGrowth?.activeUsers) ? analyticsData.userGrowth.activeUsers : [],
            totalUsers: Array.isArray(analyticsData.userGrowth?.totalUsers) ? analyticsData.userGrowth.totalUsers : []
          },
          fiveConsultants: {
            consultantData: Array.isArray(analyticsData.fiveConsultants?.consultantData) ? analyticsData.fiveConsultants.consultantData : []
          },
          consultationDistributionMonthly: {
            dates: Array.isArray(analyticsData.consultationDistributionMonthly?.dates) ? analyticsData.consultationDistributionMonthly.dates : [],
            totalAttempt: Array.isArray(analyticsData.consultationDistributionMonthly?.totalAttempt) ? analyticsData.consultationDistributionMonthly.totalAttempt : [],
            totalCompleted: Array.isArray(analyticsData.consultationDistributionMonthly?.totalCompleted) ? analyticsData.consultationDistributionMonthly.totalCompleted : [],
            firstConsultation: Array.isArray(analyticsData.consultationDistributionMonthly?.firstConsultation) ? analyticsData.consultationDistributionMonthly.firstConsultation : [],
            repeatConsultations: Array.isArray(analyticsData.consultationDistributionMonthly?.repeatConsultations) ? analyticsData.consultationDistributionMonthly.repeatConsultations : [],
            sameDayRepeatConsultation: Array.isArray(analyticsData.consultationDistributionMonthly?.sameDayRepeatConsultation) ? analyticsData.consultationDistributionMonthly.sameDayRepeatConsultation : [],
            sameDayConversionPct: Array.isArray(analyticsData.consultationDistributionMonthly?.sameDayConversionPct) ? analyticsData.consultationDistributionMonthly.sameDayConversionPct : []
          }
        });
      } else {
        toastMessage("Failed to load chart data", "error", "chartData");
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load chart data", "error", "chartData");
    } finally {
      setLoadingChartAnalytics(false);
    }
  }

  //--------------fetch-top-consultant-performance----------------------------------
  const fetchTopConsultantPerformance = async () => {
    try {
      setLoadingTopConsultants(true);
      let sendData = {};
      if (timeRange) {
        sendData.timeRange = timeRange;
      }
      if (timeRangeStartDate) {
        sendData.timeRangeStartDate = timeRangeStartDate;
      }
      if (timeRangeEndDate) {
        sendData.timeRangeEndDate = timeRangeEndDate;
      }
      sendData.limit = parseInt(topConsultantsCount) || 30;
      
      const { data } = await fetchTopConsultantPerformanceApi(sendData);
      if (data.code === 200) {
        setTopConsultantPerformance(data?.result || []);
      } else {
        toastMessage(data.message || "Failed to load top consultant performance", "error", "topConsultants");
        setTopConsultantPerformance([]);
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load top consultant performance", "error", "topConsultants");
      setTopConsultantPerformance([]);
    } finally {
      setLoadingTopConsultants(false);
    }
  };

  //--------------fetch-top-consultant-online-time----------------------------------
  const fetchTopConsultantOnlineTime = async () => {
    try {
      setLoadingTopConsultants(true);
      let sendData = {};
      if (timeRange) {
        sendData.timeRange = timeRange;
      }
      if (timeRangeStartDate) {
        sendData.timeRangeStartDate = timeRangeStartDate;
      }
      if (timeRangeEndDate) {
        sendData.timeRangeEndDate = timeRangeEndDate;
      }
      sendData.limit = parseInt(topConsultantsCount) || 30;
      
      const { data } = await fetchTopConsultantOnlineTimeApi(sendData);
      if (data.code === 200) {
        setTopConsultantOnlineTime(data?.result || []);
      } else {
        toastMessage(data.message || "Failed to load top consultant online time", "error", "topConsultants");
        setTopConsultantOnlineTime([]);
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load top consultant online time", "error", "topConsultants");
      setTopConsultantOnlineTime([]);
    } finally {
      setLoadingTopConsultants(false);
    }
  };

  //--------------fetch-user-statistics----------------------------------
  const fetchUserStatistics = async (date = null) => {
    try {
      setStatisticsLoading(true);
      const params = {};
      if (date) {
        const utcDateString = dateFormatUtc(date);
        params.date = utcDateString;
      }
      
      const { data } = await getUserStatisticsApi(params);
      if (data.code === 200 && data.result) {
        const stats = data.result;
        setUserStatistics({
          date: stats.date || "",
          dateStr: stats.dateStr || "",
          totalDownloads: stats.totalDownloads || 0,
          consulted: stats.consulted || 0,
          uniqueUsersResponded: stats.uniqueUsersResponded || 0,
          consultantResponded: stats.consultantResponded || 0,
          consultantNotResponded: stats.consultantNotResponded || 0,
          triedButNotRespondedPercentage: stats.triedButNotRespondedPercentage || 0,
          didNotTryCount: stats.didNotTryCount || 0,
          didNotTryPercentage: stats.didNotTryPercentage || 0,
          uninstall: stats.uninstall || 0,
          freeChatConsulted: stats.freeChatConsulted || 0,
        });
      }
    } catch (error) {
      console.log("Error fetching user statistics:", error);
      setUserStatistics({
        date: "",
        dateStr: "",
        totalDownloads: 0,
        consulted: 0,
        uniqueUsersResponded: 0,
        consultantResponded: 0,
        consultantNotResponded: 0,
        triedButNotRespondedPercentage: 0,
        didNotTryCount: 0,
        didNotTryPercentage: 0,
        uninstall: 0,
        freeChatConsulted: 0,
      });
    } finally {
      setStatisticsLoading(false);
    }
  };

  // Handle statistics date change
  const handleStatisticsDateChange = (date) => {
    setStatisticsDate(date);
    fetchUserStatistics(date);
  };

  //--------------fetch-user-consultant-online-gap-data----------------------------------
  const fetchUserConsultantOnlineGap = async () => {
    // Helper function to generate time intervals based on gap (fallback only)
    const generateTimeIntervals = (gap) => {
      const intervals = [];
      let intervalHours;
      
      switch(gap) {
        case '15min':
          intervalHours = 0.25;
          break;
        case '30min':
          intervalHours = 0.5;
          break;
        case '1hour':
          intervalHours = 1;
          break;
        case '2hour':
          intervalHours = 2;
          break;
        case '6hour':
          intervalHours = 6;
          break;
        case '12hour':
          intervalHours = 12;
          break;
        default:
          intervalHours = 2;
      }
      
      for (let i = 0; i < 24; i += intervalHours) {
        const startHour = Math.floor(i);
        const startMin = Math.floor((i - startHour) * 60);
        const endValue = i + intervalHours;
        const endHour = Math.floor(endValue);
        const endMin = Math.floor((endValue - endHour) * 60);
        
        const startHourStr = startHour.toString().padStart(2, '0');
        const startMinStr = startMin.toString().padStart(2, '0');
        const endHourStr = (endHour >= 24 ? endHour - 24 : endHour).toString().padStart(2, '0');
        const endMinStr = endMin.toString().padStart(2, '0');
        
        if (intervalHours >= 2) {
          intervals.push(`${startHourStr}:${startMinStr}-${endHourStr}:${endMinStr}`);
        } else {
          intervals.push(`${startHourStr}:${startMinStr}`);
        }
      }
      return intervals;
    };

    try {
      setLoadingOnlineGap(true);
      let sendData = {};
      
      // Set filter type
      if (onlineGapFilter) {
        sendData.filter = onlineGapFilter;
      }
      
      // For custom date range, include start and end dates
      if (onlineGapFilter === 'custom') {
        if (onlineGapStartDate && onlineGapEndDate) {
          sendData.startDate = onlineGapStartDate;
          sendData.endDate = onlineGapEndDate;
        } else if (onlineGapSelectedDate) {
          // Backward compatibility: single date
          sendData.date = onlineGapSelectedDate;
        }
      }
      
      // Set interval gap
      if (onlineGapInterval) {
        sendData.intervalGap = onlineGapInterval;
      }
      
      const { data } = await fetchUserConsultantOnlineGapApi(sendData);
      
      if (data.code === 200) {
        const result = data?.result || {};
        
        // Use backend intervals if available, otherwise generate based on gap
        const timeIntervals = result.timeIntervals && result.timeIntervals.length > 0 
          ? result.timeIntervals 
          : generateTimeIntervals(onlineGapInterval || '2hour');
        
        // Use backend data if available, otherwise create array matching interval count
        const intervalCount = timeIntervals.length;
        const usersOnline = Array.isArray(result.usersOnline) && result.usersOnline.length === intervalCount
          ? result.usersOnline 
          : (result.usersOnline && result.usersOnline.length > 0 ? result.usersOnline : new Array(intervalCount).fill(0));
        const consultantsOnline = Array.isArray(result.consultantsOnline) && result.consultantsOnline.length === intervalCount
          ? result.consultantsOnline 
          : (result.consultantsOnline && result.consultantsOnline.length > 0 ? result.consultantsOnline : new Array(intervalCount).fill(0));
        const missedConsultations = Array.isArray(result.missedConsultations) && result.missedConsultations.length === intervalCount
          ? result.missedConsultations 
          : (result.missedConsultations && result.missedConsultations.length > 0 ? result.missedConsultations : new Array(intervalCount).fill(0));
        const cancelledConsultations = Array.isArray(result.cancelledConsultations) && result.cancelledConsultations.length === intervalCount
          ? result.cancelledConsultations 
          : (result.cancelledConsultations && result.cancelledConsultations.length > 0 ? result.cancelledConsultations : new Array(intervalCount).fill(0));
        
        // Format date label
        let dateLabel = '';
        const now = new Date();
        
        if (onlineGapFilter === 'today') {
          dateLabel = `Today - ${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
        } else if (onlineGapFilter === 'yesterday') {
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          dateLabel = `Yesterday - ${yesterday.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
        } else if (onlineGapFilter === 'last_week') {
          const lastWeekStart = new Date(now);
          lastWeekStart.setDate(lastWeekStart.getDate() - 7);
          const lastWeekEnd = new Date(now);
          lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
          dateLabel = `Last Week (Average) - ${lastWeekStart.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} to ${lastWeekEnd.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
        } else if (onlineGapFilter === 'last_month') {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
          dateLabel = `Last Month (Average) - ${lastMonth.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;
        } else if (onlineGapFilter === 'current_year') {
          dateLabel = `Current Year (Average) - ${now.getFullYear()}`;
        } else if (onlineGapFilter === 'last_year') {
          dateLabel = `Last Year (Average) - ${now.getFullYear() - 1}`;
        } else if (onlineGapFilter === 'custom') {
          if (onlineGapStartDate && onlineGapEndDate) {
            const startDate = new Date(onlineGapStartDate);
            const endDate = new Date(onlineGapEndDate);
            dateLabel = `Custom Range (Average) - ${startDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to ${endDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
          } else if (onlineGapSelectedDate) {
            const customDate = new Date(onlineGapSelectedDate);
            dateLabel = customDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          }
        }
        
        setOnlineGapData({
          timeIntervals,
          usersOnline,
          consultantsOnline,
          missedConsultations,
          cancelledConsultations,
          dateLabel,
          currentLiveUsers: result.currentLiveUsers || 0,
          currentLiveConsultants: result.currentLiveConsultants || 0
        });
      } else {
        toastMessage(data.message || "Failed to load online gap data", "error", "onlineGap");
        // Set default empty data with correct interval count
        const defaultIntervals = generateTimeIntervals(onlineGapInterval || '2hour');
        const intervalCount = defaultIntervals.length;
        setOnlineGapData({
          timeIntervals: defaultIntervals,
          usersOnline: new Array(intervalCount).fill(0),
          consultantsOnline: new Array(intervalCount).fill(0),
          missedConsultations: new Array(intervalCount).fill(0),
          cancelledConsultations: new Array(intervalCount).fill(0),
          dateLabel: '',
          currentLiveUsers: 0,
          currentLiveConsultants: 0
        });
      }
    } catch (error) {
      console.log("error", error);
      toastMessage("Failed to load online gap data", "error", "onlineGap");
      // Set default empty data on error with correct interval count
      const defaultIntervals = generateTimeIntervals(onlineGapInterval || '2hour');
      const intervalCount = defaultIntervals.length;
      setOnlineGapData({
        timeIntervals: defaultIntervals,
        usersOnline: new Array(intervalCount).fill(0),
        consultantsOnline: new Array(intervalCount).fill(0),
        missedConsultations: new Array(intervalCount).fill(0),
        cancelledConsultations: new Array(intervalCount).fill(0),
        dateLabel: '',
        currentLiveUsers: 0,
        currentLiveConsultants: 0
      });
    } finally {
      setLoadingOnlineGap(false);
    }
  }
  //--------------change-status-sub-category------------------------
  const changeFreeTrialStatusHandler = async () => {
    try {
      buttonLoader(true);
      let sendData = { status: !status };
      const { data } = await changeFreeTrialStatus(sendData);
      if (data.code === 200) {
        getDashboardList();
        toastMessage(data.message, "success", "offerStatus");
        setStatusModal(false);
      } else {
        toastMessage(data.message, "error", "offerStatus");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      buttonLoader(false);
    }
  };

  // Fetching Dashboard Graph
  const fetchingDashboardGraphHandlder = async () => {
    try {
      showLoader();
      let sendData = {
        startDate: dateFormatUtc(startDate),
        endDate: dateFormatUtc(endDate),
      };
      const { data } = await dashboardGraphApi(sendData);
      if (data.code === 200) {
        const res = data?.result;
        const dataValue = res?.map((data) => data?.totalAmountPayToPlateform);
        // console.log(dataValue,"dataValue");
        const week = res?.map(
          (data) => `${data?._id.split("-").slice(0, 3).join("-")}`
        );
        const category = week.reverse();
        setChartData((prevChartData) => ({
          ...prevChartData,
          series: [{ name: "Revenue", data: dataValue }],
          options: {
            ...prevChartData.options,
            xaxis: { categories: category || [] },
          },
        }));
      } else {
        toastMessage(data.message, "error", "dashboardGraph");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      hideLoader();
    }
  };

  //--------------get-filter-label----------------------------------
  const getFilterLabel = (filter) => {
    switch (filter) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "last_7_days":
        return "Last 7 days";
      case "last_10_days":
        return "Last 10 days";
      case "custom":
        return "Custom Range";
      default:
        return "Last 7 days";
    }
  };

  

  //--------------handle-chart-date-filter-change----------------------------------
    // Time range handler
    const handleTimeRangeChange = (value) => {
      setTimeRange(value);
      if (value !== 'custom') {
          setTimeRangeStartDate("");
          setTimeRangeEndDate("");
      }
  };

  // Time range date change handler
  const handleTimeRangeDateChange = (type, value) => {
      if (type === 'start') {
          setTimeRangeStartDate(value);
      } else {
          setTimeRangeEndDate(value);
    }
  };

  // Refresh handler
  const handleRefresh = async () => {
      try {
          // Show loading state
          showLoader();
          
    setTimeRange("");
    setTimeRangeStartDate("");
    setTimeRangeEndDate("");
 
          // Show success message
          toastMessage("Dashboard refreshed successfully", "success", "refresh");
      } catch (error) {
          console.error("Error refreshing dashboard:", error);
          toastMessage("Failed to refresh dashboard", "error", "refresh");
    } finally {
      hideLoader();
    }
  };

  // Pagination handlers for daily breakdown table
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleChangePageHourly = (event, newPage) => {
    setPageHourly(newPage);
  };

  const handleChangeRowsPerPageHourly = (event) => {
    setRowsPerPageHourly(parseInt(event.target.value, 10));
    setPageHourly(0);
  };

  const handleChangePageGroup = (event, newPage) => {
    setPageGroup(newPage);
  };

  const handleChangeRowsPerPageGroup = (event) => {
    setRowsPerPageGroup(parseInt(event.target.value, 10));
    setPageGroup(0);
  };

  const handleChangePageRechargeFunnel = (event, newPage) => {
    setPageRechargeFunnel(newPage);
  };

  const handleChangeRowsPerPageRechargeFunnel = (event) => {
    setRowsPerPageRechargeFunnel(parseInt(event.target.value, 10));
    setPageRechargeFunnel(0);
  };

  // Calculate paginated data
  const paginatedDailyBreakdown = dailyBreakdown.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const paginatedHourlyBreakdown = hourlyBreakdown.slice(
    pageHourly * rowsPerPageHourly,
    pageHourly * rowsPerPageHourly + rowsPerPageHourly
  );

  const peakHourlyFlowScore = useMemo(() => {
    if (!hourlyBreakdown.length) return 0;
    return Math.max(...hourlyBreakdown.map(computeCustomerFlowScore), 0);
  }, [hourlyBreakdown]);

  const paginatedDailyGroupPerformance = dailyGroupPerformance.slice(
    pageGroup * rowsPerPageGroup,
    pageGroup * rowsPerPageGroup + rowsPerPageGroup
  );

  const paginatedRegistrationRechargeFunnel = registrationRechargeFunnel.rows.slice(
    pageRechargeFunnel * rowsPerPageRechargeFunnel,
    pageRechargeFunnel * rowsPerPageRechargeFunnel + rowsPerPageRechargeFunnel
  );

  const rechargeFunnelDateRangeLabel = useMemo(() =>
    formatDashboardFilterDateRangeLabel(rechargeFunnelFilter, {
      customStartDate: rechargeFunnelCustomStart,
      customEndDate: rechargeFunnelCustomEnd,
    }), [rechargeFunnelFilter, rechargeFunnelCustomStart, rechargeFunnelCustomEnd]);

  const columnDefinitions = useMemo(
    () => getDailyBreakdownColumnDefinitions(isDashboard2),
    [isDashboard2]
  );

  const hourlyColumnDefinitions = useMemo(
    () => getHourlyBreakdownColumnDefinitions(isDashboard2),
    [isDashboard2]
  );

  const groupColumnDefinitions = useMemo(
    () => getDailyGroupColumnDefinitions(isDashboard2),
    [isDashboard2]
  );

  const dailyGroupDateRangeLabel = useMemo(() => {
    if (timeRange) {
      if (timeRangeStartDate && timeRangeEndDate) {
        const start = new Date(timeRangeStartDate);
        const end = new Date(timeRangeEndDate);
        const startLabel = start.toLocaleDateString();
        const endLabel = end.toLocaleDateString();
        return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
      }
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    }
    if (isCustomDateRange && startDate && endDate) {
      const startLabel = startDate.toLocaleDateString();
      const endLabel = endDate.toLocaleDateString();
      return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
    }
    return formatDashboardFilterDateRangeLabel(dailyGroupFilter, {
      customStartDate: dailyGroupCustomStart,
      customEndDate: dailyGroupCustomEnd,
    });
  }, [
    timeRange,
    timeRangeStartDate,
    timeRangeEndDate,
    isCustomDateRange,
    startDate,
    endDate,
    dailyGroupFilter,
    dailyGroupCustomStart,
    dailyGroupCustomEnd,
  ]);

  const metricGroupBorder = (column) => {
    const style = {};
    if (column.metricGroupStart) {
      style.borderLeft = "1px solid rgba(15, 23, 42, 0.07)";
    }
    if (column.metricGroupEnd) {
      style.borderRight = "1px solid rgba(15, 23, 42, 0.22)";
    }
    return Object.keys(style).length ? style : undefined;
  };

  const renderAttemptToFirstRechargeCell = (breakdown) => (
    <span className="hourly-pct-with-uniques">
      <span>{breakdown.pct}%</span>
      <span
        className="hourly-pct-uniques"
        title="Unique 1st recharge successful"
      >
        {breakdown.uniqueFirstRecharge} unique
      </span>
    </span>
  );

  const attemptToFirstRechargeHeaderTooltip = (
    <Box sx={{ maxWidth: 420, py: 0.5 }}>
      <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
        New unique customers who attempted 1st recharge vs those who successfully recharged
      </Typography>
      <Typography variant="body2" sx={{ fontSize: 12 }}>
        (Unique 1st recharge successful ÷ Unique 1st recharge attempts) × 100. Attempts include initiated/failed/paid first-ever recharge rows; success counts only COMPLETED/paid in the same slot.
      </Typography>
    </Box>
  );

  const peakFlowHeaderTooltip = (
    <Box sx={{ maxWidth: 420, py: 0.5 }}>
      <Typography variant="body2" sx={{ fontSize: 13, mb: 1 }}>
        Compares this slot’s customer flow (unique attempts + signups) with the day’s highest slot.
      </Typography>
      {Object.values(PEAK_FLOW_LEVELS).map((level) => (
        <Box key={level.title} sx={{ mb: 0.75 }}>
          <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 700 }}>
            {level.label}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: 12, opacity: 0.92 }}>
            {level.description}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Get visible columns
  const visibleColumns = columnDefinitions.filter(col => columnVisibility[col.key]);
  const visibleHourlyColumns = hourlyColumnDefinitions.filter(col => hourlyColumnVisibility[col.key]);
  const visibleGroupColumns = groupColumnDefinitions.filter(
    (col) => columnVisibilityGroup[col.key] !== false
  );

  // Handle column visibility toggle
  const handleColumnToggle = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleHourlyColumnToggle = (columnKey) => {
    setHourlyColumnVisibility((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleColumnToggleGroup = (columnKey) => {
    setColumnVisibilityGroup((prev) => ({
      ...prev,
      [columnKey]: prev[columnKey] === false,
    }));
  };

  // Handle select all/none columns (only columns available on this dashboard variant)
  const handleSelectAllColumns = () => {
    const allVisible = columnDefinitions.every((col) => columnVisibility[col.key]);
    const newVisibility = { ...columnVisibility };
    columnDefinitions.forEach((col) => {
      newVisibility[col.key] = !allVisible;
    });
    setColumnVisibility(newVisibility);
  };

  const handleSelectAllHourlyColumns = () => {
    const allVisible = hourlyColumnDefinitions.every((col) => hourlyColumnVisibility[col.key]);
    const newVisibility = { ...hourlyColumnVisibility };
    hourlyColumnDefinitions.forEach((col) => {
      newVisibility[col.key] = !allVisible;
    });
    setHourlyColumnVisibility(newVisibility);
  };

  const handleSelectAllColumnsGroup = () => {
    const allVisible = groupColumnDefinitions.every(
      (col) => columnVisibilityGroup[col.key] !== false
    );
    const newVisibility = { ...columnVisibilityGroup };
    groupColumnDefinitions.forEach((col) => {
      newVisibility[col.key] = !allVisible;
    });
    setColumnVisibilityGroup(newVisibility);
  };

  // Helper: today's date string YYYY-MM-DD (local) for comparing with day.date
  const getTodayDateStr = () => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };
  const isTodayRow = (dateStr) => dateStr === getTodayDateStr();

  const handleDailyGroupNavigate = (row) => {
    const groupParam = getConsultantManagementGroupParam(
      row?.group ?? row?.groupName ?? row?.groupCode ?? row?.groupId
    );
    if (!groupParam) return;
    navigate(`/consultant-management?group=${encodeURIComponent(groupParam)}`);
  };

  const isDailyGroupNavColumn = (column) =>
    column.key === "group" || column.key === "totalConsultants";

  const dailyGroupNavCellStyle = {
    cursor: "pointer",
    color: "#2563eb",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  };

  // Get cell value based on column type
  const getCellValue = (day, column, dataset = []) => {
    if (isDerivedColumn(column)) {
      const derivedValue = getDerivedCellValue(day, column, dataset);
      if (column.type === 'derivedCurrency') {
        return `₹${Math.round(derivedValue || 0).toLocaleString()}`;
      }
      if (column.type === 'derivedLabel') {
        return derivedValue ?? '—';
      }
      if (column.type === 'derivedPercentage') {
        return derivedValue ?? 0;
      }
      if (column.type === 'derived') {
        return derivedValue ?? 0;
      }
    }
    switch (column.type) {
      case 'group':
        return formatGroupLabel(
          day.group ?? day.groupName ?? day.groupCode ?? day.groupId
        );
      case 'date':
        if (day.label) return day.label;
        return new Date(day.date).toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: 'short', 
          year: 'numeric' 
        });
      case 'percentage':
        return day[column.key] ?? 0;
      case 'currency':
        return `₹${(day[column.key] || 0).toLocaleString()}`;
      default:
        if (column.key === 'firstTimeRechargeCount') {
          const base = day[column.key] || 0;
          return base + (isTodayRow(day.date) ? 0 : 0);
        }
        if (column.key === 'secondRechargeCount') {
          const base = day[column.key] || 0;
          return base + (isTodayRow(day.date) ? 0 : 0);
        }
        return day[column.key] || 0;
    }
  };

  // Get totals value based on column type
  const getTotalsValue = (column, dataArray, authoritativeTotals = null) => {
    if (isDerivedColumn(column)) {
      const derivedTotal = getDerivedTotalsValue(column, dataArray);
      if (column.type === 'derivedLabel') {
        return derivedTotal;
      }
      if (column.type === 'derivedCurrency') {
        return `₹${Math.round(derivedTotal || 0).toLocaleString()}`;
      }
      if (column.type === 'derivedPercentage') {
        return derivedTotal;
      }
      if (column.type === 'derived') {
        return derivedTotal;
      }
    }
    if (
      authoritativeTotals &&
      column.key !== 'group' &&
      column.key !== 'date' &&
      Object.prototype.hasOwnProperty.call(authoritativeTotals, column.key)
    ) {
      const value = authoritativeTotals[column.key] ?? 0;
      return column.type === 'currency'
        ? `${String.fromCharCode(8377)}${Number(value).toLocaleString()}`
        : value;
    }
    switch (column.type) {
      case 'percentage': {
        if (dataArray.length === 0) return 0;
        const percentageBases = {
          sameDayConversionPct: ['sameDayRepeatConsultation', 'firstConsultation'],
          within7DayConsultationPct: ['within7DayConsultation', 'firstConsultation'],
          within14DayConsultationPct: ['within14DayConsultation', 'secondConsultation'],
          within28DayConsultationPct: ['within28DayConsultation', 'thirdConsultation'],
          sameDayFirstToSecondRechargeConversionPct: ['sameDayFirstToSecondRechargeCount', 'firstTimeRechargeCount'],
          sameDaySecondToThirdRechargeConversionPct: ['sameDaySecondToThirdRechargeCount', 'secondRechargeCount'],
        };
        const [numeratorKey, denominatorKey] = percentageBases[column.key] || [];
        if (numeratorKey && denominatorKey) {
          const numerator = dataArray.reduce((sum, row) => sum + (row[numeratorKey] || 0), 0);
          const denominator = dataArray.reduce((sum, row) => sum + (row[denominatorKey] || 0), 0);
          return denominator > 0 ? Math.round((numerator / denominator) * 10000) / 100 : 0;
        }
        if (column.key.startsWith('sameDay') && column.key.endsWith('ConversionPct')) {
          const prefix = column.key.slice(0, -'ConversionPct'.length);
          const numerator = dataArray.reduce((sum, row) => sum + (row[`${prefix}Count`] || 0), 0);
          const denominator = dataArray.reduce((sum, row) => sum + (row[`${prefix}BaseCount`] || 0), 0);
          return denominator > 0 ? Math.round((numerator / denominator) * 10000) / 100 : 0;
        }
        const sum = dataArray.reduce((s, day) => s + (day[column.key] ?? 0), 0);
        return Math.round((sum / dataArray.length) * 100) / 100;
      }
      case 'currency':
        return `₹${dataArray.reduce((sum, day) => sum + (day[column.key] || 0), 0).toLocaleString()}`;
      default:
        if (column.key === 'group' || column.type === 'group') {
          return 'TOTALS';
        }
        if (column.key === 'date') {
          return 'TOTALS';
        }
        if (column.key === 'firstTimeRechargeCount') {
          return dataArray.reduce(
            (sum, day) => sum + (day[column.key] || 0) + (isTodayRow(day.date) ? 0 : 0),
            0
          );
        }
        if (column.key === 'secondRechargeCount') {
          return dataArray.reduce(
            (sum, day) => sum + (day[column.key] || 0) + (isTodayRow(day.date) ? 0 : 0),
            0
          );
        }
        return dataArray.reduce((sum, day) => sum + (day[column.key] || 0), 0);
    }
  };

  const getInlineUniqueValue = (row, column) => {
    if (!column.uniqueKey || !row) return null;
    return row[column.uniqueKey] ?? 0;
  };

  const getInlineDifferenceValue = (totalValue, uniqueValue) => {
    const totalNumber = Number(totalValue) || 0;
    const uniqueNumber = Number(uniqueValue) || 0;
    return Math.max(0, totalNumber - uniqueNumber);
  };

  const shouldShowDifferenceBadge = (column) =>
    column.showDifferenceBadge ||
    [
      'firstConsultation',
      'secondConsultation',
      'thirdConsultation',
      'fourthConsultation',
    ].includes(column.key);

  const renderCellValue = (row, column, value) => {
    if (!column.uniqueKey || column.type === 'date' || column.type === 'group') {
      return value;
    }

    const uniqueValue = getInlineUniqueValue(row, column);
    const differenceValue = getInlineDifferenceValue(row?.[column.key], uniqueValue);
    const showDifferenceBadge = shouldShowDifferenceBadge(column);
    return (
      <span className="consultation-count-with-badge">
        <span>{value}</span>
        <span className="consultation-unique-badge" title={`${column.badgeLabel || 'Badge'}: ${uniqueValue}`}>
          {uniqueValue}
        </span>
        {showDifferenceBadge && (
          <span className="consultation-difference-badge" title={`${column.differenceBadgeLabel || 'Total - Badge'}: ${differenceValue}`}>
            {differenceValue}
          </span>
        )}
      </span>
    );
  };

  const renderTotalsCellValue = (column, value, dataArray) => {
    if (!column.uniqueKey) return value;
    const uniqueValue = dataArray.reduce((sum, day) => sum + (day[column.uniqueKey] || 0), 0);
    const totalValue = dataArray.reduce((sum, day) => sum + (day[column.key] || 0), 0);
    const differenceValue = getInlineDifferenceValue(totalValue, uniqueValue);
    const showDifferenceBadge = shouldShowDifferenceBadge(column);
    return (
      <span className="consultation-count-with-badge">
        <span>{value}</span>
        <span className="consultation-unique-badge" title={`${column.badgeLabel || 'Badge'}: ${uniqueValue}`}>
          {uniqueValue}
        </span>
        {showDifferenceBadge && (
          <span className="consultation-difference-badge" title={`${column.differenceBadgeLabel || 'Total - Badge'}: ${differenceValue}`}>
            {differenceValue}
          </span>
        )}
      </span>
    );
  };

  // console.log(chartData,"ChartData");

  //--------------click-outside-handler----------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the column selector
      if (isColumnSelectorOpen && columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setIsColumnSelectorOpen(false);
      }

      if (isColumnSelectorOpenHourly && columnSelectorRefHourly.current && !columnSelectorRefHourly.current.contains(event.target)) {
        setIsColumnSelectorOpenHourly(false);
      }

      if (isColumnSelectorOpenGroup && columnSelectorRefGroup.current && !columnSelectorRefGroup.current.contains(event.target)) {
        setIsColumnSelectorOpenGroup(false);
      }
      
      // Check if click is outside other dropdowns
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    // Always listen, but only act if dropdowns are open
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen, isColumnSelectorOpen, isColumnSelectorOpenHourly, isColumnSelectorOpenGroup]);

  useEffect(() => {
   
    // Initialize all dashboard data
    initializeDashboardData();
  }, [timeRange, timeRangeStartDate, timeRangeEndDate, isDashboard2]);

  // Refetch top consultant data when time range changes (main dashboard only)
  useEffect(() => {
    if (isDashboard2) return;
    if (sectionOpen.topConsultantOnlineTime) {
      fetchTopConsultantOnlineTime();
    }
  }, [timeRange, timeRangeStartDate, timeRangeEndDate, topConsultantsCount, sectionOpen.topConsultantOnlineTime, isDashboard2]);

  useEffect(() => {
    if (isDashboard2 || !sectionOpen.revenueAnalytics) return;
    fetchRevenueData();
  }, [timeRange, timeRangeStartDate, timeRangeEndDate, sectionOpen.revenueAnalytics, isDashboard2]);

  // Fetch online gap data when filter, date, or interval changes
  useEffect(() => {
    fetchUserConsultantOnlineGap();
  }, [onlineGapFilter, onlineGapSelectedDate, onlineGapStartDate, onlineGapEndDate, onlineGapInterval]);

  // Update current time every second for 12-hour format display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  //--------------initialize-dashboard-data----------------------------------
  const initializeDashboardData = async () => {
    try {
      showLoader();
      
      const parallel = [
        getDashboardList(),
        getSupportInsights(),
        fetchKPIData(),
        fetchDailyBreakdown(),
        fetchHourlyBreakdown(),
        fetchRegistrationRechargeFunnel(),
        fetchDailyGroupPerformance(),
        fetchUserStatistics(),
      ];
      if (sectionOpen.fiveConsultantChart || sectionOpen.consultationDistributionMonthly || sectionOpen.userGrowthChart) {
        parallel.push(fetchChartAnalyticsData());
      }
      if (!isDashboard2) {
        if (sectionOpen.revenueAnalytics) parallel.push(fetchRevenueData());
        if (sectionOpen.topConsultantOnlineTime) parallel.push(fetchTopConsultantOnlineTime());
      }
      await Promise.all(parallel);
      
    } catch (error) {
      console.log("Error initializing dashboard data:", error);
      toastMessage("Failed to load dashboard data", "error", "dashboard");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    // Only refetch data when filters change (not on initial load)
    if (!didSkipInitialDateEffectRef.current) {
      didSkipInitialDateEffectRef.current = true;
      return;
    }
    if (startDate && endDate) {
      fetchingDashboardGraphHandlder();
      fetchDailyBreakdown();
      fetchDailyGroupPerformance();
      if (sectionOpen.fiveConsultantChart || sectionOpen.consultationDistributionMonthly || sectionOpen.userGrowthChart) {
        fetchChartAnalyticsData();
      }
      if (!isDashboard2 && sectionOpen.revenueAnalytics) {
        fetchRevenueData();
      }
    }
  }, [startDate, endDate, timeFilter, isDashboard2]);

  useEffect(() => {
    if (sectionOpen.fiveConsultantChart || sectionOpen.consultationDistributionMonthly || sectionOpen.userGrowthChart) {
      fetchChartAnalyticsData();
    }
  }, [timeRange, timeRangeStartDate, timeRangeEndDate, topConsultantsCount, sectionOpen.fiveConsultantChart, sectionOpen.consultationDistributionMonthly, sectionOpen.userGrowthChart]);

  // console.log(dashboardDetail, "Dashboard Detail");

  return (
    <>
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .column-selector-dropdown {
          animation: fadeInDown 0.2s ease-out;
        }
        .column-selector-dropdown::-webkit-scrollbar {
          width: 6px;
        }
        .column-selector-dropdown::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .column-selector-dropdown::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .column-selector-dropdown::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .kpi-percentage {
          font-size: 14px;
          color: #6b7280;
          margin-top: 4px;
          font-weight: 500;
        }
        .consultant-performance-table,
        .consultant-online-time-table {
          margin-top: 20px;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .consultant-performance-table table,
        .consultant-online-time-table table {
          width: 100%;
        }
        .consultant-performance-table tbody tr:hover,
        .consultant-online-time-table tbody tr:hover {
          background-color: #f8f9fa;
        }
        .consultant-performance-table::-webkit-scrollbar,
        .consultant-online-time-table::-webkit-scrollbar {
          width: 8px;
        }
        .consultant-performance-table::-webkit-scrollbar-track,
        .consultant-online-time-table::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .consultant-performance-table::-webkit-scrollbar-thumb,
        .consultant-online-time-table::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .consultant-performance-table::-webkit-scrollbar-thumb:hover,
        .consultant-online-time-table::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .kpi-cards-container {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 20px;
        }
        .kpi-row {
          display: grid;
          gap: 15px;
        }
        .kpi-row-1 {
          grid-template-columns: 0.7fr 1.3fr;
        }
        .kpi-row-2 {
          grid-template-columns: 0.7fr 1.3fr;
          gap: 15px;
          align-items: stretch;
        }
        .kpi-row-2 .pair-box {
          height: 100%;
          display: grid;
          align-items: stretch;
          min-height: 100%;
        }
        .pair-box {
          background: #fff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(255, 137, 137, 0.1);
          border: 1px solid rgba(255, 137, 137, 0.1);
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          overflow: visible;
        }
        .pair-box-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          overflow: hidden;
          gap: 0;
        }
        .pair-box-item h4 {
          font-size: 11px;
          color: #666;
          margin: 0 0 10px 0;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          line-height: 1.4;
        }
        .pair-box-item .pair-value {
          font-size: 18px;
          font-weight: 700;
          color: #242424;
          margin: 0;
          word-break: break-word;
          overflow-wrap: break-word;
          line-height: 1.3;
          letter-spacing: -0.5px;
        }
        .pair-box-item .pair-percentage {
          font-size: 13px;
          color: #6b7280;
          margin-top: 8px;
          font-weight: 500;
          line-height: 1.4;
        }
        .pair-box-total {
          grid-column: 1 / -1;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #fecaca;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          font-weight: 600;
          color: #b91c1c;
          gap: 8px;
          overflow: visible;
        }
        .pair-box-total span:first-child {
          white-space: nowrap;
          flex-shrink: 0;
        }
        .pair-box-total span:last-child {
          padding: 2px 8px;
          border-radius: 999px;
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #b91c1c;
          min-width: 50px;
          text-align: right;
          font-size: 10px;
          flex-shrink: 0;
          overflow: visible;
          white-space: nowrap;
          display: inline-block;
        }
        .consultation-totals {
          grid-column: 1 / -1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-top: 1px;
          padding-top: 1px;
          // border-top: 1px dashed #ffb3d1;
        }
        .consultation-total-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0;
          gap: 12px;
        }
        .consultation-total-label {
          font-size: 11px;
          font-weight: 600;
          color: #b91c1c;
          letter-spacing: 0.4px;
          line-height: 1.4;
          flex: 1;
        }
        .consultation-total-value {
          font-size: 13px;
          font-weight: 700;
          color: #b91c1c;
          padding: 5px 14px;
          border-radius: 14px;
          border: 1px solid #fecaca;
          background: #fef2f2;
          min-width: 60px;
          text-align: center;
          white-space: nowrap;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .chart-section[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
          .pair-box {
            grid-template-columns: 1fr !important;
          }
          .kpi-row-1,
          .kpi-row-2 {
            grid-template-columns: 1fr !important;
          }
          .consultation-totals {
            grid-template-columns: 1fr !important;
            gap: 16px;
          }
        }
      `}</style>
      <div className="WrapperArea">
        <div className="WrapperBox">
        <div className="TitleBox">
          <h4 className="Title">
            {isDashboard2 ? "DASHBOARD" : "Dashboard"}
          </h4>
          <div className="dashboard-controls">
            <SimpleChartGrid 
              timeRange={timeRange}
              timeRangeStartDate={timeRangeStartDate}
              timeRangeEndDate={timeRangeEndDate}
              onTimeRangeChange={handleTimeRangeChange}
              onDateFilterChange={handleTimeRangeDateChange}
              onRefresh={handleRefresh}
            />
          </div>
        </div>
        <div className="dashboard">
          
          {/* KPI Indicators - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('kpiCards')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('kpiCards')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.kpiCards ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>KPI Cards (Sign up, Consultations, Recharge, etc.)</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.kpiCards ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.kpiCards && (
          <div className="kpi-cards-container">
            {loadingKPIData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: '200px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
            {/* First Row: 2 boxes */}
            <div className="kpi-row kpi-row-1">
              {/* Pair 1: User Sign up & User Sign out */}
              <div className="pair-box" style={{ gridTemplateColumns: '1.2fr 1fr', gap: '0px' }}>
                <div className="pair-box-item">
                  <h4>USER SIGN UP</h4>
                  <p className="pair-value">{kpiData.totalSignup}</p>
                  {kpiData.signupPercentage > 0 && (
                    <p className="pair-percentage">({kpiData.signupPercentage}%)</p>
                  )}
                </div>
                <div className="pair-box-item" style={{ display: 'flex', flexDirection: 'column' }}>
                  <h4>USER SIGN OUT</h4>
                  <p className="pair-value">{kpiData.totalSignout}</p>
                  {kpiData.signoutPercentage > 0 && (
                    <p className="pair-percentage">({kpiData.signoutPercentage}%)</p>
                  )}
                  {/* Total Users = Sign up + Sign out - directly below Sign Out */}
                  <div style={{ marginTop: '32px', paddingTop: '12px',display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', fontWeight: 600, color: '#b91c1c', gap: '8px' }}>
                    <span className="consultation-total-label">Total Users</span>
                    <span style={{ padding: '2px 8px', borderRadius: '999px', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', minWidth: '50px', textAlign: 'right', fontSize: '10px', flexShrink: 0, whiteSpace: 'nowrap' }}>{(kpiData.totalSignup || 0) + (kpiData.totalSignout || 0)}</span>
                  </div>
                </div>
              </div>
              
              {/* Pair 2: 1st Offer Consultation, 2nd Offer Consultation & Repeat Consultation */}
              <div className="pair-box" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', padding: '24px' }}>
                <div className="pair-box-item">
                  <h4>1st Offer Consultation</h4>
                  <p className="pair-value">{kpiData.firstOfferConsultation || 0}</p>
                  <p className="pair-percentage">
                    ({kpiData.firstOfferConversionPercentage > 0 ? kpiData.firstOfferConversionPercentage.toFixed(2) : '0.00'}%)
                  </p>
                </div>
                <div className="pair-box-item">
                  <h4>2nd Offer Consultation</h4>
                  <p className="pair-value">{kpiData.secondOfferConsultation || 0}</p>
                  <p className="pair-percentage">
                    ({kpiData.secondOfferConversionPercentage > 0 ? kpiData.secondOfferConversionPercentage.toFixed(2) : '0.00'}%)
                  </p>
                </div>
                <div className="pair-box-item">
                  <h4>4th & Onwards Consultation</h4>
                  <p className="pair-value">
                    {dailyBreakdown.reduce((sum, day) => {
                      const third = day.thirdConsultation || 0;
                      const fourth = day.fourthConsultation || 0;
                      return sum + third + fourth;
                    }, 0)}
                  </p>
                  <p className="pair-percentage" style={{ opacity: 0 }}>(0.00%)</p>
                </div>
                {/* Totals Section */}
                <div className="consultation-totals">
                  <div className="consultation-total-item">
                    <span className="consultation-total-label">Total (1st + 2nd)</span>
                    <span className="consultation-total-value">
                      {(kpiData.firstOfferConsultation || 0) + (kpiData.secondOfferConsultation || 0)}
                    </span>
                  </div>
                  <div className="consultation-total-item">
                    <span style={{ marginLeft: '100px' }} className="consultation-total-label">Total (1st + 2nd + Repeat)</span>
                    <span className="consultation-total-value">
                      {(kpiData.firstOfferConsultation || 0) + 
                       (kpiData.secondOfferConsultation || 0) + 
                       dailyBreakdown.reduce((sum, day) => {
                         const third = day.thirdConsultation || 0;
                         const fourth = day.fourthConsultation || 0;
                         return sum + third + fourth;
                       }, 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Second Row: 2 boxes */}
            <div className="kpi-row kpi-row-2">
              {/* Pair 4: Total Recharge & Total Spending */}
              <div className="pair-box" style={{ gridTemplateColumns: isDashboard2 ? '1fr' : '1.2fr 1fr', gap: '10px' }}>
                <div className="pair-box-item">
                  <h4>Total Recharge</h4>
                  <p className="pair-value">₹{Number(kpiData.totalRecharge ?? 0).toFixed(2)}</p>
                  <p className="pair-percentage" style={{ visibility: 'hidden' }}>(0%)</p>
                </div>
      {!isDashboard2 && (
                <div className="pair-box-item" style={{ display: 'flex', flexDirection: 'column' }}>
                  <h4>Total Spending</h4>
                  <p className="pair-value">₹{kpiData.totalSpending}</p>
                  <p className="pair-percentage" style={{ visibility: 'hidden' }}>(0%)</p>
                  {/* Total Transactions = Recharge + Spending - directly below Spending */}
                  <div style={{ marginTop: '28px', paddingTop: '12px', borderTop: '1px dashed #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', fontWeight: 600, color: '#b91c1c', gap: '8px' }}>
                    <span className="consultation-total-label">Total Transactions</span>
                    <span style={{ padding: '2px 8px', borderRadius: '999px', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', minWidth: '50px', textAlign: 'right', fontSize: '10px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      ₹
                      {(
                        Number(kpiData.totalRecharge || 0) +
                        Number(kpiData.totalSpending || 0)
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
                )}
              </div>
              
              {/* Pair 5: Total Install, Total Uninstall & Wallet Balance */}
              <div className="pair-box" style={{ gridTemplateColumns: isDashboard2 ? '1fr 1fr' : '0.5fr 0.5fr 0.5fr', gap: '32px', padding: '24px' }}>
                <div className="pair-box-item">
                  <h4>Total Install</h4>
                  <p className="pair-value">{kpiData.totalInstall}</p>
                  <p className="pair-percentage" style={{ visibility: 'hidden' }}>(0%)</p>
                </div>
                <div className="pair-box-item">
                  <h4>Total Uninstall</h4>
                  <p className="pair-value">{kpiData.totalUninstall}</p>
                  <p className="pair-percentage" style={{ visibility: 'hidden' }}>(0%)</p>
                </div>
                {!isDashboard2 && (
                <div className="pair-box-item">
                  <h4>Wallet Balance</h4>
                  <p className="pair-value">₹{kpiData.totalWalletBalance}</p>
                  <p className="pair-percentage" style={{ visibility: 'hidden' }}>(0%)</p>
                </div>
                )}
                {/* Totals Section */}
                {!isDashboard2 && (
                <div className="consultation-totals">
                  <div className="consultation-total-item">
                    <span className="consultation-total-label">Total Downloads</span>
                    <span className="consultation-total-value">
                      {(kpiData.totalInstall || 0) + (kpiData.totalUninstall || 0)}
                    </span>
                  </div>
                  <div className="consultation-total-item">
                    <span style={{ marginLeft: '120px' }} className="consultation-total-label">Wallet Balance</span>
                    <span className="consultation-total-value">
                      ₹{kpiData.totalWalletBalance}
                    </span>
                  </div>
                </div>
                )}
              </div>
            </div>

            {!isDashboard2 && (
            <div className="kpi-row" style={{ marginTop: '16px' ,marginLeft: '40px'}}>
              <div className="pair-box" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', padding: '24px' }}>
                <div className="pair-box-item">
                  <h4>Within 7 Day - User Count</h4>
                  <p className="pair-value" style={{ fontSize: '20px', fontWeight: 700, color: '#b91c1c',marginLeft: '60px'}}>
                    {within7DayUserCount}
                  </p>
                </div>
                <div className="pair-box-item">
                  <h4>Within 14 Day - User Count</h4>
                  <p className="pair-value" style={{ fontSize: '20px', fontWeight: 700, color: '#b91c1c',marginLeft: '60px'}}>
                    {within14DayUserCount}
                  </p>
                </div>
                <div className="pair-box-item">
                  <h4>Within 28 Day - User Count</h4>
                  <p className="pair-value" style={{ fontSize: '20px', fontWeight: 700, color: '#b91c1c',marginLeft: '60px'}}>
                    {within28DayUserCount}
                  </p>
                </div>
              </div>
            </div>
            )}
            </>
            )}
          </div>
            )}
          </div>

          {/* User Statistics Section - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('userStatistics')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('userStatistics')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.userStatistics ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>User Statistics</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.userStatistics ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.userStatistics && (
          <div className="user-statistics-section" style={{
            marginBottom: '20px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <div>
                <h5 style={{ margin: 0, color: '#000', fontWeight: '700', fontSize: '18px' }}>
                  User Statistics
                </h5>
                {userStatistics.date && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '15px', color: '#333', fontWeight: '500' }}>
                    Date: {userStatistics.date}
                  </p>
                )}
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <div style={{ minWidth: '200px' }}>
                  <DatePicker
                    selected={statisticsDate}
                    onChange={handleStatisticsDateChange}
                    placeholder="Select Date (Optional)"
                    className="form-control"
                  />
                </div>
                {statisticsDate && (
                  <SecondaryButton 
                    onClick={() => {
                      setStatisticsDate(null);
                      fetchUserStatistics(null);
                    }}
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    Clear Date
                  </SecondaryButton>
                )}
              </div>
            </div>
            {statisticsLoading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                padding: '40px'
              }}>
                <Loader />
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '15px'
              }}>
                {/* Total Downloads */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '12px',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #1976d2',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '700' }}>
                    Total Downloads
                  </div>
                  <div style={{ fontSize: '30px', fontWeight: 'bold', color: '#1565c0', lineHeight: '1.2' }}>
                    {(userStatistics.totalDownloads + userStatistics.uninstall).toLocaleString()}
                  </div>
                </div>

                {/* Consultant Responded */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '12px',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #0288d1',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>
                    Consultant Responded
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#0288d1', lineHeight: '1.2' }}>
                      {userStatistics.uniqueUsersResponded.toLocaleString()}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>|</span>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                      {userStatistics.uniqueUsersResponded > 0 && userStatistics.totalDownloads > 0
                        ? Math.min(((userStatistics.uniqueUsersResponded / (userStatistics.totalDownloads + userStatistics.uninstall)) * 100), 100).toFixed(1) 
                        : '0'}%
                    </div>
                  </div>
                </div>

                {/* Tried but not responded */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '12px',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #ed6c02',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>
                    Tried but not responded
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#e65100', lineHeight: '1.2' }}>
                      {userStatistics.consultantNotResponded.toLocaleString()}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>|</span>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                      {userStatistics.totalDownloads > 0 
                        ? Math.min(((userStatistics.consultantNotResponded / (userStatistics.totalDownloads + userStatistics.uninstall)) * 100), 100).toFixed(1) 
                        : '0'}%
                    </div>
                  </div>
                </div>

                {/* Did Not Even Try */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '12px',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #d32f2f',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>
                    Did Not Even Try
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#d32f2f', lineHeight: '1.2' }}>
                      {userStatistics.didNotTryCount.toLocaleString()}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>|</span>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                      {userStatistics.totalDownloads > 0 
                        ? Math.min(((userStatistics.didNotTryCount / (userStatistics.totalDownloads + userStatistics.uninstall)) * 100), 100).toFixed(1) 
                        : '0'}%
                    </div>
                  </div>
                </div>

                {/* Uninstall Rate */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '12px',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #6c757d',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '500' }}>
                    {isDashboard2 ? 'Account Delete Rate' : 'Uninstall Rate'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                    <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#6c757d', lineHeight: '1.2' }}>
                      {userStatistics.uninstall.toLocaleString()}
                    </div>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>|</span>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                      {userStatistics.totalDownloads > 0 
                        ? Math.min(((userStatistics.uninstall / (userStatistics.totalDownloads + userStatistics.uninstall)) * 100), 100).toFixed(1) 
                        : '0'}%
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
            )}
          </div>

          {/* Live Online - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('liveOnline')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('liveOnline')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.liveOnline ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>Live Users & Consultants Online</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.liveOnline ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.liveOnline && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            marginBottom: '20px', 
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ 
              flex: 1, 
              padding: '16px', 
              backgroundColor: '#fff', 
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #3b82f6'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  <i className="fa fa-users"></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Live Users Online
                  </div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#242424',
                    marginTop: '4px'
                  }}>
                    {loadingOnlineGap ? (
                      <CircularProgress size={20} />
                    ) : (
                      onlineGapData.currentLiveUsers || 0
                    )}
                  </div>
                </div>
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: '#999', 
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fa fa-clock-o" style={{ fontSize: '12px' }}></i>
                <span>{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
              </div>
            </div>
            
            <div style={{ 
              flex: 1, 
              padding: '16px', 
              backgroundColor: '#fff', 
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #10b981'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  <i className="fa fa-user-md"></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Live Consultants Online
                  </div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#242424',
                    marginTop: '4px'
                  }}>
                    {loadingOnlineGap ? (
                      <CircularProgress size={20} />
                    ) : (
                      onlineGapData.currentLiveConsultants || 0
                    )}
                  </div>
                </div>
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: '#999', 
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className="fa fa-clock-o" style={{ fontSize: '12px' }}></i>
                <span>{currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
              </div>
            </div>
          </div>
            )}
          </div>


          
            {/* Daily Breakdown - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('dailyBreakdown')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('dailyBreakdown')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.dailyBreakdown ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>Daily Breakdown</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.dailyBreakdown ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.dailyBreakdown && (
            <div className="daily-breakdown-section">
            <div className="breakdown-header">
              <h4>Daily Breakdown</h4>
              <div className="breakdown-header-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div className="breakdown-summary" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <span className="summary-item">
                    <i className="fa fa-calendar"></i>
                    {dailyBreakdown.length > 0
                      ? (() => {
                          const dates = dailyBreakdown.map((d) => new Date(d.date).getTime());
                          const minDate = new Date(Math.min(...dates));
                          const maxDate = new Date(Math.max(...dates));
                          return `${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;
                        })()
                      : `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                    }
                  </span>
                  <span className="summary-item">
                    <i className="fa fa-chart-line"></i>
                    {dailyBreakdown.length} days of data
                  </span>
                  {/* Show last X days selector - when no global time range, user picks 2, 3, 10, 20, 30 */}
                  {!timeRange && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="daily-breakdown-days-label">Show last</InputLabel>
                      <MuiSelect
                        labelId="daily-breakdown-days-label"
                        value={dailyBreakdownDays}
                        label="Show last"
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setDailyBreakdownDays(value);
                          setPage(0);
                          fetchDailyBreakdown(value);
                        }}
                        sx={{ height: '40px', background: 'white' }}
                      >
                        {[2, 3, 10, 20, 30].map((n) => (
                          <MenuItem key={n} value={n}>
                            {n} days
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  )}
                </div>
                <div className="column-selector-wrapper" ref={columnSelectorRef} style={{ position: 'relative' }}>
                  <button 
                    type="button"
                    className="column-selector-btn"
                    onClick={() => {
                      setIsColumnSelectorOpen(prev => !prev);
                    }}
                    style={{
                      padding: '10px 18px',
                      background: 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  >
                    <i className="fa fa-columns" style={{ fontSize: '16px', color: '#6b7280' }}></i>
                    <span>Columns</span>
                    <span style={{
                      background: '#f3f4f6',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: '1'
                    }}>
                      {visibleColumns.length}/{columnDefinitions.length}
                    </span>
                  </button>
                  {isColumnSelectorOpen && (
                    <div 
                      className="column-selector-dropdown"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                        padding: '0',
                        minWidth: '320px',
                        maxWidth: '400px',
                        zIndex: 1000,
                        maxHeight: '500px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Header */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '16px 20px',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#f9fafb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <i className="fa fa-sliders" style={{ color: '#6b7280', fontSize: '16px' }}></i>
                          <strong style={{ fontSize: '16px', color: '#111827', fontWeight: '600' }}>
                            Select Columns
                          </strong>
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAllColumns}
                          style={{
                            background: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#374151',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                            e.currentTarget.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                        >
                          {columnDefinitions.every((col) => columnVisibility[col.key]) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      
                      {/* Scrollable Content */}
                      <div style={{
                        maxHeight: '420px',
                        overflowY: 'auto',
                        padding: '8px',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e1 #f1f5f9'
                      }}>
                        {columnDefinitions.map((column, index) => {
                          const isChecked = columnVisibility[column.key];
                          const getColumnIcon = (type) => {
                            switch(type) {
                              case 'date': return 'fa-calendar';
                              case 'group': return 'fa-users';
                              case 'currency': return 'fa-rupee-sign';
                              case 'percentage': return 'fa-percent';
                              default: return 'fa-chart-bar';
                            }
                          };
                          
                          return (
                            <label
                              key={column.key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                marginBottom: '2px',
                                transition: 'background 0.2s ease',
                                background: isChecked ? '#f3f4f6' : 'transparent',
                                border: '1px solid transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isChecked ? '#f3f4f6' : 'transparent';
                              }}
                            >
                              <div style={{ 
                                position: 'relative',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleColumnToggle(column.key)}
                                  style={{ 
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#6b7280'
                                  }}
                                />
                              </div>
                              <i 
                                className={`fa ${getColumnIcon(column.type)}`}
                                style={{
                                  marginRight: '12px',
                                  color: '#9ca3af',
                                  fontSize: '14px',
                                  width: '18px',
                                  textAlign: 'center'
                                }}
                              ></i>
                              <span style={{ 
                                fontSize: '14px', 
                                color: '#374151',
                                fontWeight: '400',
                                flex: 1
                              }}>
                                {column.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      
                      {/* Footer */}
                      <div style={{
                        padding: '12px 20px',
                        borderTop: '1px solid #e5e7eb',
                        background: '#f9fafb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
                          {visibleColumns.length} of {columnDefinitions.length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => setIsColumnSelectorOpen(false)}
                          style={{
                            background: 'white',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            padding: '6px 16px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                            e.currentTarget.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loadingDailyBreakdown ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className="table-container">
                  <table className="daily-breakdown-table">
                    <thead>
                      <tr>
                        {visibleColumns.map((column) => {
                          return (
                          <th
                            key={column.key}
                            style={metricGroupBorder(column)}
                          >
                            <Tooltip
                              arrow
                              placement="top"
                              enterDelay={250}
                              title={
                                <Box sx={{ maxWidth: 380, py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                                    {column.description}
                                  </Typography>
                                  <Typography variant="caption" component="div" sx={{ mt: 1, display: "block", opacity: 0.92 }}>
                                    <strong>Example:</strong> {column.example}
                                  </Typography>
                                </Box>
                              }
                            >
                              <span style={{ cursor: "help", borderBottom: "1px dotted #94a3b8" }}>
                                {column.label}
                              </span>
                            </Tooltip>
                          </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {dailyBreakdown.length > 0 ? (
                        paginatedDailyBreakdown
                          .map((day, index) => (
                            <tr key={`${day.date}-${index}`}>
                              {visibleColumns.map((column) => {
                                const cellValue = getCellValue(day, column);
                                const cellClassName = column.type === 'date' || column.type === 'group'
                                  ? 'date-cell' 
                                  : column.type === 'currency' 
                                  ? 'currency-cell' 
                                  : `number-cell ${column.className || ''}`;
                                return (
                                  <td
                                    key={column.key}
                                    className={cellClassName}
                                    style={metricGroupBorder(column)}
                                  >
                                    {column.type === 'date' || column.type === 'group' ? (
                                      <div className="date-info">
                                        <span className="date-text">{cellValue}</span>
                                      </div>
                                    ) : column.type === 'percentage' ? (
                                      `${cellValue}%`
                                    ) : (
                                      renderCellValue(day, column, cellValue)
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={visibleColumns.length} className="no-data">
                            <div className="no-data-content">
                              <i className="fa fa-chart-bar"></i>
                              <p>No data available for the selected period</p>
                              <small>Try selecting a different time range</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {dailyBreakdown.length > 0 && (
                      <tfoot>
                        <tr className="totals-row">
                          {visibleColumns.map((column) => {
                            const totalsValue = getTotalsValue(column, dailyBreakdown);
                            const cellClassName = column.type === 'date' || column.type === 'group'
                              ? 'totals-label' 
                              : column.type === 'currency' 
                              ? 'totals-currency' 
                              : `totals-value ${column.className || ''}`;
                            return (
                              <td
                                key={column.key}
                                className={cellClassName}
                                style={metricGroupBorder(column)}
                              >
                                {column.type === 'percentage'
                                  ? `${totalsValue}%`
                                  : renderTotalsCellValue(column, totalsValue, dailyBreakdown)}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {dailyBreakdown.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2, pb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, dailyBreakdown.length)} of {dailyBreakdown.length} entries
                    </Typography>
                    <TablePagination
                      component="div"
                      count={dailyBreakdown.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </Box>
                )}
              </>
            )}
          </div>
            )}
          </div>


















            {/* Daily Group Performance - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('dailyGroupPerformance')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('dailyGroupPerformance')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.dailyGroupPerformance ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>Daily Group Performance</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.dailyGroupPerformance ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.dailyGroupPerformance && (
            <div className="daily-breakdown-section">
            <div className="breakdown-header">
              <h4>Daily Group Performance</h4>
              <div className="breakdown-header-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div className="breakdown-summary" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <span className="summary-item">
                    <i className="fa fa-calendar"></i>
                    {dailyGroupDateRangeLabel}
                  </span>
                  <span className="summary-item">
                    <i className="fa fa-chart-line"></i>
                    {dailyGroupPerformance.length} groups
                  </span>
                  {!timeRange && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="daily-group-days-label">Show last</InputLabel>
                        <MuiSelect
                          labelId="daily-group-days-label"
                          value={dailyGroupFilter}
                          label="Show last"
                          onChange={(e) => {
                            const value = e.target.value;
                            setDailyGroupFilter(value);
                            setPageGroup(0);
                            if (value !== "custom") {
                              setDailyGroupCustomStart(null);
                              setDailyGroupCustomEnd(null);
                              fetchDailyGroupPerformance(value);
                            }
                          }}
                          sx={{ height: '40px', background: 'white' }}
                        >
                          {DASHBOARD_DAY_RANGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                      {dailyGroupFilter === "custom" && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <DatePicker
                            selected={dailyGroupCustomStart}
                            onChange={(date) => {
                              setDailyGroupCustomStart(date);
                              if (date && dailyGroupCustomEnd) {
                                setPageGroup(0);
                                fetchDailyGroupPerformance("custom", {
                                  start: date,
                                  end: dailyGroupCustomEnd,
                                });
                              }
                            }}
                            maxDate={dailyGroupCustomEnd || new Date()}
                            placeholderText="Start date"
                            className="form-control"
                            dateFormat="dd-MM-yyyy"
                          />
                          <DatePicker
                            selected={dailyGroupCustomEnd}
                            onChange={(date) => {
                              setDailyGroupCustomEnd(date);
                              if (dailyGroupCustomStart && date) {
                                setPageGroup(0);
                                fetchDailyGroupPerformance("custom", {
                                  start: dailyGroupCustomStart,
                                  end: date,
                                });
                              }
                            }}
                            minDate={dailyGroupCustomStart}
                            maxDate={new Date()}
                            placeholderText="End date"
                            className="form-control"
                            dateFormat="dd-MM-yyyy"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="column-selector-wrapper" ref={columnSelectorRefGroup} style={{ position: 'relative' }}>
                  <button 
                    type="button"
                    className="column-selector-btn"
                    onClick={() => {
                      setIsColumnSelectorOpenGroup(prev => !prev);
                    }}
                    style={{
                      padding: '10px 18px',
                      background: 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f9fafb';
                      e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  >
                    <i className="fa fa-columns" style={{ fontSize: '16px', color: '#6b7280' }}></i>
                    <span>Columns</span>
                    <span style={{
                      background: '#f3f4f6',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: '1'
                    }}>
                      {visibleGroupColumns.length}/{groupColumnDefinitions.length}
                    </span>
                  </button>
                  {isColumnSelectorOpenGroup && (
                    <div 
                      className="column-selector-dropdown"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
                        padding: '0',
                        minWidth: '320px',
                        maxWidth: '400px',
                        zIndex: 1000,
                        maxHeight: '500px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '16px 20px',
                        borderBottom: '1px solid #e5e7eb',
                        background: '#f9fafb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <i className="fa fa-sliders" style={{ color: '#6b7280', fontSize: '16px' }}></i>
                          <strong style={{ fontSize: '16px', color: '#111827', fontWeight: '600' }}>
                            Select Columns
                          </strong>
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectAllColumnsGroup}
                          style={{
                            background: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            color: '#374151',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                            e.currentTarget.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                        >
                          {groupColumnDefinitions.every((col) => columnVisibilityGroup[col.key] !== false) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div style={{
                        maxHeight: '420px',
                        overflowY: 'auto',
                        padding: '8px',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e1 #f1f5f9'
                      }}>
                        {groupColumnDefinitions.map((column) => {
                          const isChecked = columnVisibilityGroup[column.key] !== false;
                          const getColumnIcon = (type) => {
                            switch(type) {
                              case 'date': return 'fa-calendar';
                              case 'group': return 'fa-users';
                              case 'currency': return 'fa-rupee-sign';
                              case 'percentage': return 'fa-percent';
                              default: return 'fa-chart-bar';
                            }
                          };
                          
                          return (
                            <label
                              key={column.key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                marginBottom: '2px',
                                transition: 'background 0.2s ease',
                                background: isChecked ? '#f3f4f6' : 'transparent',
                                border: '1px solid transparent'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isChecked ? '#f3f4f6' : 'transparent';
                              }}
                            >
                              <div style={{ 
                                position: 'relative',
                                marginRight: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleColumnToggleGroup(column.key)}
                                  style={{ 
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#6b7280'
                                  }}
                                />
                              </div>
                              <i 
                                className={`fa ${getColumnIcon(column.type)}`}
                                style={{
                                  marginRight: '12px',
                                  color: '#9ca3af',
                                  fontSize: '14px',
                                  width: '18px',
                                  textAlign: 'center'
                                }}
                              ></i>
                              <span style={{ 
                                fontSize: '14px', 
                                color: '#374151',
                                fontWeight: '400',
                                flex: 1
                              }}>
                                {column.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <div style={{
                        padding: '12px 20px',
                        borderTop: '1px solid #e5e7eb',
                        background: '#f9fafb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '400' }}>
                          {visibleGroupColumns.length} of {groupColumnDefinitions.length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => setIsColumnSelectorOpenGroup(false)}
                          style={{
                            background: 'white',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            padding: '6px 16px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                            e.currentTarget.style.borderColor = '#9ca3af';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#d1d5db';
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loadingDailyGroupPerformance ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className="table-container">
                  <table className="daily-breakdown-table">
                    <thead>
                      <tr>
                        {visibleGroupColumns.map((column) => {
                          return (
                          <th
                            key={column.key}
                            style={metricGroupBorder(column)}
                          >
                            <Tooltip
                              arrow
                              placement="top"
                              enterDelay={250}
                              title={
                                <Box sx={{ maxWidth: 380, py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                                    {column.description}
                                  </Typography>
                                  <Typography variant="caption" component="div" sx={{ mt: 1, display: "block", opacity: 0.92 }}>
                                    <strong>Example:</strong> {column.example}
                                  </Typography>
                                </Box>
                              }
                            >
                              <span style={{ cursor: "help", borderBottom: "1px dotted #94a3b8" }}>
                                {column.label}
                              </span>
                            </Tooltip>
                          </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {dailyGroupPerformance.length > 0 ? (
                        paginatedDailyGroupPerformance
                          .map((day, index) => (
                            <tr key={`group-${day.group ?? index}-${index}`}>
                              {visibleGroupColumns.map((column) => {
                                const cellValue = getCellValue(day, column);
                                const isNavCell = isDailyGroupNavColumn(column);
                                const cellClassName = column.type === 'date' || column.type === 'group'
                                  ? 'date-cell' 
                                  : column.type === 'currency' 
                                  ? 'currency-cell' 
                                  : `number-cell ${column.className || ''}`;
                                return (
                                  <td
                                    key={column.key}
                                    className={cellClassName}
                                    style={metricGroupBorder(column)}
                                    onClick={isNavCell ? () => handleDailyGroupNavigate(day) : undefined}
                                    role={isNavCell ? "link" : undefined}
                                    tabIndex={isNavCell ? 0 : undefined}
                                    onKeyDown={
                                      isNavCell
                                        ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              handleDailyGroupNavigate(day);
                                            }
                                          }
                                        : undefined
                                    }
                                  >
                                    {column.type === 'date' || column.type === 'group' ? (
                                      <div className="date-info">
                                        <span
                                          className="date-text"
                                          style={isNavCell ? dailyGroupNavCellStyle : undefined}
                                        >
                                          {cellValue}
                                        </span>
                                      </div>
                                    ) : column.type === 'percentage' ? (
                                      `${cellValue}%`
                                    ) : isNavCell ? (
                                      <span style={dailyGroupNavCellStyle}>{cellValue}</span>
                                    ) : (
                                      renderCellValue(day, column, cellValue)
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={visibleGroupColumns.length} className="no-data">
                            <div className="no-data-content">
                              <i className="fa fa-chart-bar"></i>
                              <p>No data available for the selected period</p>
                              <small>Try selecting a different time range</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {dailyGroupPerformance.length > 0 && (
                      <tfoot>
                        <tr className="totals-row">
                          {visibleGroupColumns.map((column) => {
                            const totalsValue = getTotalsValue(
                              column,
                              dailyGroupPerformance,
                              dailyGroupTotals
                            );
                            const cellClassName = column.type === 'date' || column.type === 'group'
                              ? 'totals-label' 
                              : column.type === 'currency' 
                              ? 'totals-currency' 
                              : `totals-value ${column.className || ''}`;
                            return (
                              <td
                                key={column.key}
                                className={cellClassName}
                                style={metricGroupBorder(column)}
                              >
                                {column.type === 'percentage'
                                  ? `${totalsValue}%`
                                  : renderTotalsCellValue(column, totalsValue, dailyGroupPerformance)}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {dailyGroupPerformance.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2, pb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {pageGroup * rowsPerPageGroup + 1} to {Math.min((pageGroup + 1) * rowsPerPageGroup, dailyGroupPerformance.length)} of {dailyGroupPerformance.length} entries
                    </Typography>
                    <TablePagination
                      component="div"
                      count={dailyGroupPerformance.length}
                      page={pageGroup}
                      onPageChange={handleChangePageGroup}
                      rowsPerPage={rowsPerPageGroup}
                      onRowsPerPageChange={handleChangeRowsPerPageGroup}
                      rowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </Box>
                )}
              </>
            )}
          </div>
            )}
          </div>

            {/* Hourly Breakdown - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('hourlyBreakdown')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('hourlyBreakdown')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.hourlyBreakdown ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>Hourly Breakdown</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.hourlyBreakdown ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.hourlyBreakdown && (
            <div className="daily-breakdown-section">
            <div className="breakdown-header">
              <h4>Hourly Breakdown</h4>
              <div className="breakdown-header-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div className="breakdown-summary" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <span className="summary-item">
                    <i className="fa fa-calendar"></i>
                    {(hourlySelectedDate || new Date()).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="summary-item">
                    <i className="fa fa-clock-o"></i>
                    {hourlyBreakdown.length} intervals
                  </span>
                  <DatePicker
                    selected={hourlySelectedDate}
                    onChange={(date) => {
                      if (!date) return;
                      setHourlySelectedDate(date);
                      setPageHourly(0);
                      fetchHourlyBreakdown(date);
                    }}
                    maxDate={new Date()}
                    placeholderText="Select date"
                    className="form-control"
                    dateFormat="dd-MM-yyyy"
                  />
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="hourly-breakdown-interval-label">Time interval</InputLabel>
                    <MuiSelect
                      labelId="hourly-breakdown-interval-label"
                      value={hourlyInterval}
                      label="Time interval"
                      onChange={(e) => {
                        const value = e.target.value;
                        setHourlyInterval(value);
                        setPageHourly(0);
                        fetchHourlyBreakdown(null, value);
                      }}
                      sx={{ height: '40px', background: 'white' }}
                    >
                      {HOURLY_INTERVAL_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                </div>
                <div className="column-selector-wrapper" ref={columnSelectorRefHourly} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="column-selector-btn"
                    onClick={() => setIsColumnSelectorOpenHourly((prev) => !prev)}
                    style={{
                      padding: '10px 18px',
                      background: 'white',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <i className="fa fa-columns" style={{ fontSize: '16px', color: '#6b7280' }}></i>
                    <span>Columns</span>
                    <span style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', color: '#6b7280' }}>
                      {visibleHourlyColumns.length}/{hourlyColumnDefinitions.length}
                    </span>
                  </button>
                  {isColumnSelectorOpenHourly && (
                    <div
                      className="column-selector-dropdown"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        minWidth: '320px',
                        zIndex: 1000,
                        maxHeight: '500px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <strong style={{ fontSize: '16px', color: '#111827' }}>Select Columns</strong>
                        <button
                          type="button"
                          onClick={handleSelectAllHourlyColumns}
                          style={{ background: 'white', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}
                        >
                          {hourlyColumnDefinitions.every((col) => hourlyColumnVisibility[col.key]) ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div style={{ maxHeight: '420px', overflowY: 'auto', padding: '8px' }}>
                        {hourlyColumnDefinitions.map((column) => {
                          const isChecked = hourlyColumnVisibility[column.key];
                          return (
                            <label
                              key={column.key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                background: isChecked ? '#f3f4f6' : 'transparent',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleHourlyColumnToggle(column.key)}
                                style={{ width: '18px', height: '18px', marginRight: '12px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '14px', color: '#374151' }}>
                                {column.key === 'date' ? 'Time' : column.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {loadingHourlyBreakdown ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className="table-container">
                  <table className="daily-breakdown-table">
                    <thead>
                      <tr>
                        {visibleHourlyColumns.map((column) => (
                          <th key={column.key} style={metricGroupBorder(column)}>
                            <Tooltip
                              arrow
                              placement="top"
                              enterDelay={250}
                              title={
                                column.key === 'peakFlowIndicator'
                                  ? peakFlowHeaderTooltip
                                  : column.key === 'attemptToFirstRechargePct'
                                  ? attemptToFirstRechargeHeaderTooltip
                                  : (
                                <Box sx={{ maxWidth: 380, py: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                                    {column.key === 'date'
                                      ? 'Time bucket in Asia/Kolkata for the selected interval.'
                                      : column.description}
                                  </Typography>
                                </Box>
                                  )
                              }
                            >
                              <span style={{ cursor: "help", borderBottom: "1px dotted #94a3b8" }}>
                                {column.key === 'date' ? 'Time' : column.label}
                              </span>
                            </Tooltip>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hourlyBreakdown.length > 0 ? (
                        paginatedHourlyBreakdown.map((row, index) => {
                          const isFutureInterval = Boolean(row.isFutureInterval);
                          const isPeakRow =
                            !isFutureInterval &&
                            peakHourlyFlowScore > 0 &&
                            computeCustomerFlowScore(row) === peakHourlyFlowScore;
                          return (
                          <tr
                            key={`${row.date}-${row.bucketIndex}-${index}`}
                            style={isPeakRow
                              ? { background: 'rgba(251, 191, 36, 0.12)' }
                              : isFutureInterval
                                ? { background: '#fafafa', color: '#9ca3af' }
                                : undefined}
                          >
                            {visibleHourlyColumns.map((column) => {
                              const cellValue = getCellValue(row, column, hourlyBreakdown);
                              const cellClassName = column.type === 'date' || column.type === 'group'
                                ? 'date-cell'
                                : column.type === 'currency' || column.type === 'derivedCurrency'
                                ? 'currency-cell'
                                : column.type === 'derivedLabel'
                                ? 'date-cell'
                                : `number-cell ${column.className || ''}`;
                              const peakMeta =
                                column.key === 'peakFlowIndicator'
                                  ? getPeakFlowMeta(row, hourlyBreakdown)
                                  : null;
                              let cellContent;
                              if (column.type === 'date' || column.type === 'group') {
                                cellContent = (
                                  <div className="date-info">
                                    <span className="date-text">{cellValue}</span>
                                    {isFutureInterval ? <small style={{ display: 'block', fontWeight: 400 }}>Future interval</small> : null}
                                  </div>
                                );
                              } else if (isFutureInterval) {
                                cellContent = '—';
                              } else if (column.key === 'attemptToFirstRechargePct') {
                                cellContent = renderAttemptToFirstRechargeCell(
                                  getAttemptToFirstRechargeBreakdown(row)
                                );
                              } else if (column.type === 'percentage' || column.type === 'derivedPercentage') {
                                cellContent = `${cellValue}%`;
                              } else if (column.key === 'peakFlowIndicator' && peakMeta?.level) {
                                cellContent = (
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    enterDelay={200}
                                    title={
                                      <Box sx={{ maxWidth: 340, py: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                                          {peakMeta.label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                                          {peakMeta.description}
                                        </Typography>
                                      </Box>
                                    }
                                  >
                                    <span className={`peak-flow-label peak-flow-${peakMeta.level}`}>
                                      {peakMeta.label}
                                    </span>
                                  </Tooltip>
                                );
                              } else {
                                cellContent = renderCellValue(row, column, cellValue);
                              }
                              return (
                                <td
                                  key={column.key}
                                  className={cellClassName}
                                  style={metricGroupBorder(column)}
                                >
                                  {cellContent}
                                </td>
                              );
                            })}
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={visibleHourlyColumns.length} className="no-data">
                            <div className="no-data-content">
                              <i className="fa fa-clock-o"></i>
                              <p>No hourly data available for the selected period</p>
                              <small>Try selecting a different time range or interval</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {hourlyBreakdown.length > 0 && (
                      <tfoot>
                        <tr className="totals-row">
                          {visibleHourlyColumns.map((column) => {
                            const totalsValue = getTotalsValue(column, hourlyBreakdown);
                            const cellClassName = column.type === 'date' || column.type === 'group'
                              ? 'totals-label'
                              : column.type === 'currency'
                              ? 'totals-currency'
                              : `totals-value ${column.className || ''}`;
                            return (
                              <td
                                key={column.key}
                                className={cellClassName}
                                style={metricGroupBorder(column)}
                              >
                                {column.key === 'attemptToFirstRechargePct'
                                  ? renderAttemptToFirstRechargeCell(
                                      getAttemptToFirstRechargeTotals(hourlyBreakdown)
                                    )
                                  : column.type === 'percentage' || column.type === 'derivedPercentage'
                                  ? `${totalsValue}%`
                                  : renderTotalsCellValue(column, totalsValue, hourlyBreakdown)}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {hourlyBreakdown.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2, pb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {pageHourly * rowsPerPageHourly + 1} to {Math.min((pageHourly + 1) * rowsPerPageHourly, hourlyBreakdown.length)} of {hourlyBreakdown.length} entries
                    </Typography>
                    <TablePagination
                      component="div"
                      count={hourlyBreakdown.length}
                      page={pageHourly}
                      onPageChange={handleChangePageHourly}
                      rowsPerPage={rowsPerPageHourly}
                      onRowsPerPageChange={handleChangeRowsPerPageHourly}
                      rowsPerPageOptions={[12, 24, 36, 48, 96]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </Box>
                )}
              </>
            )}
          </div>
            )}
          </div>

      {!isDashboard2 && (
        <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => toggleSection('registrationRechargeFunnel')}
            onKeyDown={(e) => e.key === 'Enter' && toggleSection('registrationRechargeFunnel')}
            style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.registrationRechargeFunnel ? '1px solid #e5e7eb' : 'none' }}
          >
            <span>Registration → Recharge (24-hour funnel)</span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.registrationRechargeFunnel ? '▼ Hide' : '▶ Show'}</span>
          </div>
          {sectionOpen.registrationRechargeFunnel && (
            <div style={{ padding: '16px' }}>
              <div className="breakdown-header" style={{ marginBottom: '16px' }}>
                <div className="breakdown-header-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div className="breakdown-summary" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <span className="summary-item"><i className="fa fa-calendar"></i>{rechargeFunnelDateRangeLabel}</span>
                    <span className="summary-item"><i className="fa fa-chart-line"></i>{registrationRechargeFunnel.rows.length} days of data</span>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="recharge-funnel-filter-label">Show last</InputLabel>
                      <MuiSelect
                        labelId="recharge-funnel-filter-label"
                        value={rechargeFunnelFilter}
                        label="Show last"
                        onChange={(e) => {
                          const value = e.target.value;
                          setRechargeFunnelFilter(value);
                          setPageRechargeFunnel(0);
                          if (value !== "custom") {
                            setRechargeFunnelCustomStart(null);
                            setRechargeFunnelCustomEnd(null);
                            fetchRegistrationRechargeFunnel(value);
                          }
                        }}
                        sx={{ height: '40px', background: 'white' }}
                      >
                        {DASHBOARD_DAY_RANGE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                    {rechargeFunnelFilter === "custom" && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <DatePicker
                          selected={rechargeFunnelCustomStart}
                          onChange={(date) => {
                            setRechargeFunnelCustomStart(date);
                            if (date && rechargeFunnelCustomEnd) {
                              setPageRechargeFunnel(0);
                              fetchRegistrationRechargeFunnel("custom", { start: date, end: rechargeFunnelCustomEnd });
                            }
                          }}
                          maxDate={rechargeFunnelCustomEnd || new Date()}
                          placeholderText="Start date"
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                        />
                        <DatePicker
                          selected={rechargeFunnelCustomEnd}
                          onChange={(date) => {
                            setRechargeFunnelCustomEnd(date);
                            if (rechargeFunnelCustomStart && date) {
                              setPageRechargeFunnel(0);
                              fetchRegistrationRechargeFunnel("custom", { start: rechargeFunnelCustomStart, end: date });
                            }
                          }}
                          minDate={rechargeFunnelCustomStart}
                          maxDate={new Date()}
                          placeholderText="End date"
                          className="form-control"
                          dateFormat="dd-MM-yyyy"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Same-day means IST calendar day; later steps use rolling 24-hour windows.</Typography>
              {loadingRegistrationRechargeFunnel ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><CircularProgress /></Box>
              ) : (
                <>
                <div className="table-container">
                <table className="daily-breakdown-table">
                  <thead>
                    <tr>
                      <th>Date (IST)</th>
                      <th><Tooltip arrow title="Distinct non-test, non-deleted users registered on this IST date"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>Registered users</span></Tooltip></th>
                      <th><Tooltip arrow title="Users whose first-ever successful recharge happened on the same IST calendar date as registration"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>Registered & recharged same day</span></Tooltip></th>
                      <th><Tooltip arrow title="Users registered on this row's date whose first-ever successful recharge falls anywhere inside the selected dashboard period"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>Registered & 1st recharge in period</span></Tooltip></th>
                      <th><Tooltip arrow title="Successful same-day recharge transactions; one user can contribute more than one"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>Same-day recharge count</span></Tooltip></th>
                      <th><Tooltip arrow title="Same-day cohort users whose second successful recharge occurred within 24 hours of their first"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>2nd within 24h of 1st</span></Tooltip></th>
                      <th><Tooltip arrow title="Users passing the previous step whose third successful recharge occurred within 24 hours of their second"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>3rd within 24h of 2nd</span></Tooltip></th>
                      <th><Tooltip arrow title="Sum of paidAmount for all successful recharges made on the registration date"><span style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>Same-day recharge amount</span></Tooltip></th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrationRechargeFunnel.rows.length > 0 ? paginatedRegistrationRechargeFunnel.map((row) => (
                      <tr key={row.date}>
                        <td className="date-cell"><div className="date-info"><span className="date-text">{row.date}</span></div></td>
                        <td className="number-cell">{Number(row.registeredUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="number-cell">{Number(row.sameDayFirstRechargeUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="number-cell">{Number(row.registeredAndFirstRechargeInSelectedPeriodUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="number-cell">{Number(row.sameDayRechargeCount || 0).toLocaleString('en-IN')}</td>
                        <td className="number-cell">{Number(row.secondRechargeWithin24HoursUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="number-cell">{Number(row.thirdRechargeWithin24HoursUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="currency-cell">₹ {Number(row.sameDayRechargeAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={8} className="no-data"><div className="no-data-content"><i className="fa fa-chart-bar"></i><p>No data available for the selected period</p><small>Try selecting a different time range</small></div></td></tr>
                    )}
                  </tbody>
                  {registrationRechargeFunnel.totals && registrationRechargeFunnel.rows.length > 0 && (
                    <tfoot>
                      <tr className="totals-row">
                        <td className="totals-label">Total</td>
                        <td className="totals-value">{Number(registrationRechargeFunnel.totals.registeredUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="totals-value">{Number(registrationRechargeFunnel.totals.sameDayFirstRechargeUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="totals-value">{Number(registrationRechargeFunnel.totals.registeredAndFirstRechargeInSelectedPeriodUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="totals-value">{Number(registrationRechargeFunnel.totals.sameDayRechargeCount || 0).toLocaleString('en-IN')}</td>
                        <td className="totals-value">{Number(registrationRechargeFunnel.totals.secondRechargeWithin24HoursUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="totals-value">{Number(registrationRechargeFunnel.totals.thirdRechargeWithin24HoursUsers || 0).toLocaleString('en-IN')}</td>
                        <td className="totals-currency">₹ {Number(registrationRechargeFunnel.totals.sameDayRechargeAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
                </div>
                {registrationRechargeFunnel.rows.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2, pb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {pageRechargeFunnel * rowsPerPageRechargeFunnel + 1} to {Math.min((pageRechargeFunnel + 1) * rowsPerPageRechargeFunnel, registrationRechargeFunnel.rows.length)} of {registrationRechargeFunnel.rows.length} entries
                    </Typography>
                    <TablePagination component="div" count={registrationRechargeFunnel.rows.length} page={pageRechargeFunnel} onPageChange={handleChangePageRechargeFunnel} rowsPerPage={rowsPerPageRechargeFunnel} onRowsPerPageChange={handleChangeRowsPerPageRechargeFunnel} rowsPerPageOptions={[5, 10, 20, 31, 50, 100]} labelRowsPerPage="Rows per page:" />
                  </Box>
                )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {!isDashboard2 && (
        <>
      <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection('topConsultantOnlineTime')}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('topConsultantOnlineTime')}
          style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.topConsultantOnlineTime ? '1px solid #e5e7eb' : 'none' }}
        >
          <span>Top Consultants Online Time</span>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.topConsultantOnlineTime ? '▼ Hide' : '▶ Show'}</span>
        </div>
        {sectionOpen.topConsultantOnlineTime && (
          <div className="chart-row">
            <div className="chart-item full-width">
              {loadingTopConsultants ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ConsultantOnlineTimeChart
                  data={topConsultantOnlineTime}
                  topN={topConsultantsCount}
                  onTopNChange={(v) => setTopConsultantsCount(Number(v))}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection('fiveConsultantChart')}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('fiveConsultantChart')}
          style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.fiveConsultantChart ? '1px solid #e5e7eb' : 'none' }}
        >
          <span>Top 5 Consultants Chart</span>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.fiveConsultantChart ? '▼ Hide' : '▶ Show'}</span>
        </div>
        {sectionOpen.fiveConsultantChart && (
          <div className="chart-row">
            <div className="chart-item full-width">
              {loadingChartAnalytics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <FiveConsultantChart
                  data={chartAnalyticsData?.fiveConsultants}
                  topN={topConsultantsCount}
                  onTopNChange={(v) => setTopConsultantsCount(Number(v))}
                />
              )}
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Consultation Distribution Monthly - accordion */}
      <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection('consultationDistributionMonthly')}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('consultationDistributionMonthly')}
          style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.consultationDistributionMonthly ? '1px solid #e5e7eb' : 'none' }}
        >
          <span>Consultation Distribution Monthly</span>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.consultationDistributionMonthly ? '▼ Hide' : '▶ Show'}</span>
        </div>
        {sectionOpen.consultationDistributionMonthly && (
      <div className="chart-row">
        <div className="chart-item full-width">
          {loadingChartAnalytics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <ConsultationDistributionMonthlyChart data={chartAnalyticsData?.consultationDistributionMonthly} />
          )}
        </div>
      </div>
        )}
      </div>

      {/* User and Consultant Online Gap Chart - accordion */}
      <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection('userConsultantOnlineGap')}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('userConsultantOnlineGap')}
          style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.userConsultantOnlineGap ? '1px solid #e5e7eb' : 'none' }}
        >
          <span>User/Consultant Online Gap Chart</span>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.userConsultantOnlineGap ? '▼ Hide' : '▶ Show'}</span>
        </div>
        {sectionOpen.userConsultantOnlineGap && (
      <div className="chart-row">
        <div className="chart-item full-width">
          {loadingOnlineGap ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <UserConsultantOnlineGapChart
              data={onlineGapData}
              filter={onlineGapFilter}
              onFilterChange={(value) => {
                setOnlineGapFilter(value);
                if (value !== 'custom') {
                  setOnlineGapSelectedDate(null);
                  setOnlineGapStartDate(null);
                  setOnlineGapEndDate(null);
                }
              }}
              onDateChange={(date) => setOnlineGapSelectedDate(date)}
              onStartDateChange={(date) => setOnlineGapStartDate(date)}
              onEndDateChange={(date) => setOnlineGapEndDate(date)}
              selectedDate={onlineGapSelectedDate}
              startDate={onlineGapStartDate}
              endDate={onlineGapEndDate}
              intervalGap={onlineGapInterval}
              onIntervalChange={(value) => setOnlineGapInterval(value)}
            />
          )}
        </div>
      </div>
        )}
      </div>

          {/* User Growth Chart - accordion */}
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('userGrowthChart')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('userGrowthChart')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.userGrowthChart ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>User Growth Chart</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.userGrowthChart ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.userGrowthChart && (
          <div className="chart-section" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="chart-item">
          {loadingChartAnalytics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <UserGrowthChart data={chartAnalyticsData?.userGrowth}  />
          )}
        </div>
          </div>
            )}
          </div>

          {!isDashboard2 && (
          <div style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleSection('revenueAnalytics')}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection('revenueAnalytics')}
              style={{ padding: '12px 16px', background: '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: '15px', color: '#111827', borderBottom: sectionOpen.revenueAnalytics ? '1px solid #e5e7eb' : 'none' }}
            >
              <span>Revenue & Spending Analytics</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{sectionOpen.revenueAnalytics ? '▼ Hide' : '▶ Show'}</span>
            </div>
            {sectionOpen.revenueAnalytics && (
              <div className="chart-row">
                <div className="chart-item full-width">
                  {loadingRevenueData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ReactApexChart
                      options={chartData.options}
                      series={chartData.series}
                      type="area"
                      height={350}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          )}

        </div>

        <ModalBox
          status={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          description="Do you want to change status ?"
          action={changeFreeTrialStatusHandler}
          ref={buttonref}
        />
      </div>
    </div>
    </>
  );
};

export default Dashboard;
